/**
 * NewSaleForm Refactorizado
 *
 * Este archivo muestra cómo usar los componentes compartidos.
 * Puede reemplazar gradualmente el código en NewSaleForm.js
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, Package, Truck, Plus, X, Search, Minus, Trash2, CreditCard, MapPin, Edit3, History } from 'lucide-react';
import { Autocomplete } from '@react-google-maps/api';
import { useApp } from '../../context/AppContext';
import { useGoogleMaps } from '../../components/maps/GoogleMapsLoader';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';

// Componentes compartidos nuevos
import useCart from '../../hooks/useCart';
import usePermissions from '../../hooks/usePermissions';
import QuantityModal from '../../components/sales/QuantityModal';
import PaymentModal from '../../components/sales/PaymentModal';
import PrintOrderModal from '../../components/sales/PrintOrderModal';

// Componentes existentes
import apiService from '../../services/apiService';
import electronService from '../../services/electron/electronService';
import { formatMoney } from '../../utils/formatters';
import './NewSaleForm.css';

// Función para verificar si la unidad de medida requiere decimales (kilo/gramo)
const esUnidadPeso = (unidad) => {
    if (!unidad) return false;
    const unidadLower = unidad.toLowerCase().trim();
    return ['kilo', 'kilos', 'kg', 'kilogramo', 'kilogramos', 'gramo', 'gramos', 'g', 'gr'].includes(unidadLower);
};

// Abreviar unidades de medida para formatos de impresión
const abreviarUnidad = (unidad) => {
    if (!unidad) return 'PZA';
    const map = {
        'pieza': 'PZA', 'piezas': 'PZA', 'pz': 'PZA', 'pza': 'PZA',
        'kilogramo': 'KG', 'kilogramos': 'KG', 'kilo': 'KG', 'kilos': 'KG', 'kg': 'KG',
        'gramo': 'GR', 'gramos': 'GR', 'g': 'GR', 'gr': 'GR',
        'caja': 'CJA', 'cajas': 'CJA', 'cja': 'CJA',
        'paquete': 'PQT', 'paquetes': 'PQT', 'pqt': 'PQT',
        'bulto': 'BLT', 'bultos': 'BLT', 'blt': 'BLT',
        'millar': 'MIL', 'millares': 'MIL', 'mil': 'MIL',
        'litro': 'LT', 'litros': 'LT', 'lt': 'LT', 'l': 'LT',
        'metro': 'MT', 'metros': 'MT', 'mt': 'MT', 'm': 'MT',
        'rollo': 'RLL', 'rollos': 'RLL', 'rll': 'RLL',
        'bolsa': 'BLS', 'bolsas': 'BLS', 'bls': 'BLS',
        'costal': 'CST', 'costales': 'CST', 'cst': 'CST',
    };
    return map[unidad.toLowerCase().trim()] || unidad.toUpperCase().substring(0, 3);
};

const NewSaleFormRefactored = ({ onClose, onSaleCreated, onPausar, pedidoInicial }) => {
    const { loading, setLoading, showToast, user } = useApp();
    const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps();
    const { hasPermission } = usePermissions();

    // Wizard steps
    const [currentStep, setCurrentStep] = useState(1);

    // Cliente (obligatorio para ventas)
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneSearching, setPhoneSearching] = useState(false);
    const [cliente, setCliente] = useState(null);
    const [showQuickRegister, setShowQuickRegister] = useState(false);
    const [quickRegisterData, setQuickRegisterData] = useState({
        nombre: '',
        nombre_comercial: '',
        precio_venta: 'precio_lista',
        telefonos: [{ telefono: '', tipo: 'principal' }],
        direcciones: [{ direccion: '', latitud: null, longitud: null, es_principal: true }]
    });
    const [quickRegisterSearchResults, setQuickRegisterSearchResults] = useState([]);
    const [showQuickRegisterResults, setShowQuickRegisterResults] = useState(false);

    // Entrega
    const [deliveryType, setDeliveryType] = useState('');
    const [selectedAddress, setSelectedAddress] = useState(null);

    // Configuraciones
    const [configImpresion, setConfigImpresion] = useState({
        impresion_preguntar_ubicacion: true,
        impresion_ubicacion_default: 'local'
    });
    const [comisionTarjeta, setComisionTarjeta] = useState(0);
    const [campoCosto, setCampoCosto] = useState('precio_compra');
    const [preguntarCantidad, setPreguntarCantidad] = useState(true);
    const [empresaLogo, setEmpresaLogo] = useState(null);
    const [empresaNombre, setEmpresaNombre] = useState('');
    const [empresaFormatoColor, setEmpresaFormatoColor] = useState('#1e40af');
    const [empresaFormatoMostrarLogo, setEmpresaFormatoMostrarLogo] = useState(true);
    const [empresaFormatoInfoEmpresa, setEmpresaFormatoInfoEmpresa] = useState('');
    const [empresaFormatoMarcaAgua, setEmpresaFormatoMarcaAgua] = useState(true);
    const [empresaFormatoMarcaAguaOpacidad, setEmpresaFormatoMarcaAguaOpacidad] = useState(5);
    const [empresaFormatoMostrarFirma, setEmpresaFormatoMostrarFirma] = useState(false);
    const [empresaFormatoDesgloseIva, setEmpresaFormatoDesgloseIva] = useState(false);
    const [empresaFormatoPiePagina, setEmpresaFormatoPiePagina] = useState('');
    const [empresaFormatoMarcaAguaTamano, setEmpresaFormatoMarcaAguaTamano] = useState(500);
    const [empresaFormatoCopiaOriginal, setEmpresaFormatoCopiaOriginal] = useState(true);
    const [empresaFormatoCopiaCliente, setEmpresaFormatoCopiaCliente] = useState(false);
    const [empresaFormatoCopiaMostrador, setEmpresaFormatoCopiaMostrador] = useState(false);
    const [empresaFormatoLogoTamano, setEmpresaFormatoLogoTamano] = useState(100);
    const [empresaFormatoInfoTamano, setEmpresaFormatoInfoTamano] = useState(9);
    const [empresaFormatoClienteTamano, setEmpresaFormatoClienteTamano] = useState(10);
    const [empresaFormatoPagare, setEmpresaFormatoPagare] = useState(false);
    const [empresaFormatoPagareTexto, setEmpresaFormatoPagareTexto] = useState('');
    const [imprimirPedido, setImprimirPedido] = useState(true);
    const [tipoImpresionPedido, setTipoImpresionPedido] = useState('formato');
    const [impresoraPedidos, setImpresoraPedidos] = useState('');
    const [impresoraPredeterminada, setImpresoraPredeterminada] = useState('');

    // Comisiones de envío
    const [comisionesEnvio, setComisionesEnvio] = useState([]);
    const [comisionEnvioSeleccionada, setComisionEnvioSeleccionada] = useState(null);

    // Crédito
    const [creditoDisponible, setCreditoDisponible] = useState(0);
    const [metodoPago, setMetodoPago] = useState('efectivo');
    const [estatusPago, setEstatusPago] = useState('pendiente');

    // Hook del carrito compartido
    const {
        cart,
        setCart,
        selectedCartItemId,
        setSelectedCartItemId,
        getCustomerPrice,
        getCustomerPriceFromPresentacion,
        addToCartDirectly,
        addToCartWithQuantity,
        removeFromCart,
        deleteFromCart,
        updateQuantity,
        updateQuantityDebounced,
        changePriceLevel,
        cambiarPresentacion,
        clearCart,
        loadEquivalencias,
        totals
    } = useCart({
        cliente,
        comisionTarjeta,
        showToast
    });

    // Estados de UI
    const [observaciones, setObservaciones] = useState('');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [ventaParaImprimir, setVentaParaImprimir] = useState(null);

    // Estados para QuantityModal
    const [quantityModalData, setQuantityModalData] = useState(null);
    const [equivalenciasDisponibles, setEquivalenciasDisponibles] = useState([]);
    const [loadingEquivalencias, setLoadingEquivalencias] = useState(false);

    // Estado para PriceSelector
    const [priceSelector, setPriceSelector] = useState(null);
    const [selectedPriceIndex, setSelectedPriceIndex] = useState(0);

    // Estado para precio libre (doble F4)
    const [showPrecioLibreModal, setShowPrecioLibreModal] = useState(false);
    const [precioLibreData, setPrecioLibreData] = useState({ precio: '', itemId: null, precioCosto: 0, productoNombre: '' });
    const [showPasswordPrecioLibreModal, setShowPasswordPrecioLibreModal] = useState(false);
    const [passwordPrecioLibre, setPasswordPrecioLibre] = useState('');
    const [verificandoPasswordPrecioLibre, setVerificandoPasswordPrecioLibre] = useState(false);
    const [usuarioAutorizadorPrecioLibre, setUsuarioAutorizadorPrecioLibre] = useState(null);
    const passwordPrecioLibreInputRef = useRef(null);
    const precioLibreInputRef = useRef(null);
    const lastF4PressRef = useRef(0);

    // Modal de historial producto-cliente (Ctrl+H)
    const [showHistorialModal, setShowHistorialModal] = useState(false);
    const [historialData, setHistorialData] = useState([]);
    const [historialProductoNombre, setHistorialProductoNombre] = useState('');
    const [historialCostoActual, setHistorialCostoActual] = useState(0);
    const [historialCostoTipoActual, setHistorialCostoTipoActual] = useState('');
    const [historialItemId, setHistorialItemId] = useState(null);
    const [loadingHistorial, setLoadingHistorial] = useState(false);

    // Estado para selector de direcciones en paso 3
    const [showAddressSelector, setShowAddressSelector] = useState(false);

    // Estados para búsqueda de productos
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedSearchResultIndex, setSelectedSearchResultIndex] = useState(0);

    // Estados para búsqueda de clientes
    const [clientSearchResults, setClientSearchResults] = useState([]);
    const [showClientResults, setShowClientResults] = useState(false);
    const [selectedClientResultIndex, setSelectedClientResultIndex] = useState(0);

    // Estados para direcciones
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

    // Refs
    const searchInputRef = useRef(null);
    const phoneInputRef = useRef(null);
    const searchDebounceTimer = useRef(null);
    const quickRegisterNameRef = useRef(null);
    const autocompleteInstances = useRef({});
    const searchResultsContainerRef = useRef(null);
    const clientResultsContainerRef = useRef(null);
    const cursorTimeoutRef = useRef(null);

    // Estado para ocultar cursor
    const [cursorHidden, setCursorHidden] = useState(false);

    // Ocultar cursor cuando no se mueve
    useEffect(() => {
        const handleMouseMove = () => {
            setCursorHidden(false);
            if (cursorTimeoutRef.current) {
                clearTimeout(cursorTimeoutRef.current);
            }
            cursorTimeoutRef.current = setTimeout(() => {
                setCursorHidden(true);
            }, 2000); // Ocultar después de 2 segundos sin movimiento
        };

        window.addEventListener('mousemove', handleMouseMove);

        // Iniciar el timer al abrir
        cursorTimeoutRef.current = setTimeout(() => {
            setCursorHidden(true);
        }, 2000);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            if (cursorTimeoutRef.current) {
                clearTimeout(cursorTimeoutRef.current);
            }
        };
    }, []);

    // Cargar pedido inicial si existe
    useEffect(() => {
        if (pedidoInicial) {
            setCurrentStep(pedidoInicial.currentStep || 3);
            setCliente(pedidoInicial.cliente || null);
            setDeliveryType(pedidoInicial.deliveryType || '');
            setSelectedAddress(pedidoInicial.selectedAddress || null);
            setCart(pedidoInicial.cart || []);
            setMetodoPago(pedidoInicial.metodoPago || 'efectivo');
            setObservaciones(pedidoInicial.observaciones || '');
            if (pedidoInicial.cliente?.telefonos?.[0]?.telefono) {
                setPhoneNumber(pedidoInicial.cliente.telefonos[0].telefono);
            }
        }
    }, [pedidoInicial, setCart]);

    // Cargar configuraciones
    useEffect(() => {
        const cargarConfiguraciones = async () => {
            try {
                const nombrePc = localStorage.getItem('pos_nombre_pc') || null;
                const [impresionResult, cajaResult, empresaResult] = await Promise.all([
                    apiService.getConfiguracionesPorCategoria('impresion', nombrePc),
                    apiService.getConfiguracionesPorCategoria('caja', nombrePc),
                    apiService.getConfiguracionesPorCategoria('empresa', null)
                ]);

                if (impresionResult.success && impresionResult.data?.valores) {
                    setConfigImpresion(impresionResult.data.valores);
                    setImpresoraPredeterminada(impresionResult.data.valores.impresion_impresora_predeterminada || '');
                }

                if (cajaResult.success && cajaResult.data?.valores) {
                    const cajaVals = cajaResult.data.valores;
                    const preguntarCant = cajaVals.caja_preguntar_cantidad;
                    setPreguntarCantidad(preguntarCant === true || preguntarCant === 'true' || preguntarCant === undefined);
                    const impPedido = cajaVals.caja_imprimir_pedido;
                    setImprimirPedido(impPedido !== false && impPedido !== 'false');
                    setTipoImpresionPedido(cajaVals.caja_tipo_impresion_pedido || 'formato');
                    setImpresoraPedidos(cajaVals.caja_impresora_pedidos || '');
                }

                if (empresaResult.success && empresaResult.data?.valores) {
                    const valores = empresaResult.data.valores;
                    const comision = parseFloat(valores.empresa_comision_tarjeta) || 0;
                    setComisionTarjeta(comision);

                    // Cargar logo de empresa
                    if (valores.empresa_logo) {
                        setEmpresaLogo(valores.empresa_logo);
                    }

                    // Cargar comisiones de envío disponibles
                    const envios = [];
                    if (valores.empresa_comision_envio_1 && parseFloat(valores.empresa_comision_envio_1) > 0) {
                        envios.push({ id: 1, valor: parseFloat(valores.empresa_comision_envio_1) });
                    }
                    if (valores.empresa_comision_envio_2 && parseFloat(valores.empresa_comision_envio_2) > 0) {
                        envios.push({ id: 2, valor: parseFloat(valores.empresa_comision_envio_2) });
                    }
                    if (valores.empresa_comision_envio_3 && parseFloat(valores.empresa_comision_envio_3) > 0) {
                        envios.push({ id: 3, valor: parseFloat(valores.empresa_comision_envio_3) });
                    }
                    setComisionesEnvio(envios);

                    // Campo de costo a usar
                    setCampoCosto(valores.empresa_campo_costo || 'precio_compra');

                    // Nombre de empresa
                    if (valores.empresa_nombre) setEmpresaNombre(valores.empresa_nombre);

                    // Formato de impresión
                    if (valores.empresa_formato_color) setEmpresaFormatoColor(valores.empresa_formato_color);
                    setEmpresaFormatoMostrarLogo(valores.empresa_formato_mostrar_logo !== false && valores.empresa_formato_mostrar_logo !== 'false');
                    if (valores.empresa_formato_info_empresa) setEmpresaFormatoInfoEmpresa(valores.empresa_formato_info_empresa);
                    setEmpresaFormatoMarcaAgua(valores.empresa_formato_marca_agua !== false && valores.empresa_formato_marca_agua !== 'false');
                    if (valores.empresa_formato_marca_agua_opacidad) setEmpresaFormatoMarcaAguaOpacidad(parseFloat(valores.empresa_formato_marca_agua_opacidad) || 5);
                    setEmpresaFormatoMostrarFirma(valores.empresa_formato_mostrar_firma === true || valores.empresa_formato_mostrar_firma === 'true');
                    setEmpresaFormatoDesgloseIva(valores.empresa_formato_desglose_iva === true || valores.empresa_formato_desglose_iva === 'true');
                    if (valores.empresa_formato_pie_pagina) setEmpresaFormatoPiePagina(valores.empresa_formato_pie_pagina);
                    if (valores.empresa_formato_marca_agua_tamano) setEmpresaFormatoMarcaAguaTamano(parseFloat(valores.empresa_formato_marca_agua_tamano) || 500);
                    setEmpresaFormatoCopiaOriginal(valores.empresa_formato_copia_original !== false && valores.empresa_formato_copia_original !== 'false');
                    setEmpresaFormatoCopiaCliente(valores.empresa_formato_copia_cliente === true || valores.empresa_formato_copia_cliente === 'true');
                    setEmpresaFormatoCopiaMostrador(valores.empresa_formato_copia_mostrador === true || valores.empresa_formato_copia_mostrador === 'true');
                    if (valores.empresa_formato_logo_tamano) setEmpresaFormatoLogoTamano(parseInt(valores.empresa_formato_logo_tamano) || 100);
                    if (valores.empresa_formato_info_tamano) setEmpresaFormatoInfoTamano(parseInt(valores.empresa_formato_info_tamano) || 9);
                    if (valores.empresa_formato_cliente_tamano) setEmpresaFormatoClienteTamano(parseInt(valores.empresa_formato_cliente_tamano) || 10);
                    setEmpresaFormatoPagare(valores.empresa_formato_pagare === true || valores.empresa_formato_pagare === 'true');
                    if (valores.empresa_formato_pagare_texto) setEmpresaFormatoPagareTexto(valores.empresa_formato_pagare_texto);
                }
            } catch (error) {
                console.error('Error cargando configuraciones:', error);
            }
        };
        cargarConfiguraciones();
    }, []);

    // Calcular crédito disponible
    useEffect(() => {
        if (cliente && cliente.tiene_credito) {
            const montoMaximo = parseFloat(cliente.monto_credito_maximo || 0);
            const saldoUsado = parseFloat(cliente.saldo_credito_usado || 0);
            const disponible = montoMaximo - saldoUsado;
            setCreditoDisponible(disponible > 0 ? disponible : 0);
        } else {
            setCreditoDisponible(0);
        }
    }, [cliente]);

    // Focus según paso
    useEffect(() => {
        if (currentStep === 1 && !showQuickRegister && phoneInputRef.current) {
            phoneInputRef.current.focus();
        } else if (currentStep === 3 && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [currentStep, showQuickRegister]);

    // Búsqueda de productos con debounce
    useEffect(() => {
        if (searchQuery.length >= 2) {
            if (searchDebounceTimer.current) {
                clearTimeout(searchDebounceTimer.current);
            }
            searchDebounceTimer.current = setTimeout(async () => {
                try {
                    const result = await apiService.buscarProductos(searchQuery);
                    if (result.success) {
                        const productos = result.data || [];
                        // Ordenar resultados por relevancia
                        const queryNorm = searchQuery.trim().toLowerCase();
                        const queryWords = queryNorm.split(/\s+/);
                        const normalizarTexto = (txt) => txt.toLowerCase().replace(/#\s*/g, '').replace(/\b0+(\d)/g, '$1');
                        const queryNormNum = normalizarTexto(queryNorm);

                        const scored = productos.map(p => {
                            const nombre = (p.nombre || '').toLowerCase();
                            const nombreNorm = normalizarTexto(nombre);
                            let score = 0;

                            if (nombreNorm.includes(queryNormNum)) score += 100;
                            if (nombreNorm.startsWith(queryNormNum)) score += 50;
                            const todasPresentes = queryWords.every(w => {
                                const wNorm = normalizarTexto(w);
                                return nombreNorm.includes(wNorm);
                            });
                            if (todasPresentes) score += 30;
                            score -= nombre.length * 0.1;

                            return { ...p, _score: score };
                        });

                        scored.sort((a, b) => b._score - a._score);

                        setSearchResults(scored);
                        setShowResults(true);
                    }
                } catch (error) {
                    console.error('Error buscando productos:', error);
                }
            }, 300);
        } else {
            setSearchResults([]);
            setShowResults(false);
        }
        setSelectedSearchResultIndex(0);

        return () => {
            if (searchDebounceTimer.current) {
                clearTimeout(searchDebounceTimer.current);
            }
        };
    }, [searchQuery]);

    // Búsqueda de clientes con debounce
    useEffect(() => {
        if (phoneNumber.length >= 3 && currentStep === 1) {
            const timer = setTimeout(async () => {
                try {
                    const result = await apiService.buscarClientes(phoneNumber);
                    if (result.success && result.data?.length > 0) {
                        setClientSearchResults(result.data);
                        setShowClientResults(true);
                    } else {
                        setClientSearchResults([]);
                        setShowClientResults(false);
                    }
                } catch (error) {
                    console.error('Error buscando clientes:', error);
                }
            }, 300);
            return () => clearTimeout(timer);
        } else {
            setClientSearchResults([]);
            setShowClientResults(false);
        }
        setSelectedClientResultIndex(0);
    }, [phoneNumber, currentStep]);

    // Atajos de teclado
    useEffect(() => {
        const handleKeyDown = (e) => {
            // No procesar si hay modales abiertos
            if (quantityModalData || showPaymentModal || priceSelector || showHistorialModal) {
                return;
            }

            // === PASO 1: Cliente ===
            if (currentStep === 1) {
                // F2: Continuar sin cliente
                if (e.key === 'F2') {
                    e.preventDefault();
                    setCliente(null);
                    setPhoneNumber('');
                    setCurrentStep(2);
                }

                // Navegación de resultados de clientes
                if (showClientResults && clientSearchResults.length > 0) {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setSelectedClientResultIndex(prev =>
                            Math.min(prev + 1, clientSearchResults.length - 1)
                        );
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setSelectedClientResultIndex(prev => Math.max(prev - 1, 0));
                    }
                    if (e.key === 'Enter' && clientSearchResults[selectedClientResultIndex]) {
                        e.preventDefault();
                        const clienteSeleccionado = clientSearchResults[selectedClientResultIndex];
                        setCliente(clienteSeleccionado);
                        setShowClientResults(false);
                        // showToast('Cliente seleccionado: ' + clienteSeleccionado.nombre, 'success');
                        setCurrentStep(2);
                    }
                }

                // Escape: Cerrar
                if (e.key === 'Escape') {
                    e.preventDefault();
                    onClose();
                }
            }

            // === PASO 2: Tipo de entrega ===
            if (currentStep === 2) {
                // 1 o D: Domicilio
                if (e.key === '1' || e.key === 'd' || e.key === 'D') {
                    e.preventDefault();
                    setDeliveryType('domicilio');
                    setTimeout(() => setCurrentStep(3), 300);
                }
                // 2 o P: Pickup/Recoger
                if (e.key === '2' || e.key === 'p' || e.key === 'P') {
                    e.preventDefault();
                    setDeliveryType('pickup');
                    setSelectedAddress(null);
                    setTimeout(() => setCurrentStep(3), 300);
                }
                // Navegación de direcciones (funciona si hay direcciones, y selecciona domicilio automáticamente)
                if (cliente?.direcciones?.length > 0) {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        // Seleccionar domicilio automáticamente al navegar direcciones
                        if (deliveryType !== 'domicilio') {
                            setDeliveryType('domicilio');
                        }
                        const newIndex = Math.min(selectedAddressIndex + 1, cliente.direcciones.length - 1);
                        setSelectedAddressIndex(newIndex);
                        setSelectedAddress(cliente.direcciones[newIndex]);
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        // Seleccionar domicilio automáticamente al navegar direcciones
                        if (deliveryType !== 'domicilio') {
                            setDeliveryType('domicilio');
                        }
                        const newIndex = Math.max(selectedAddressIndex - 1, 0);
                        setSelectedAddressIndex(newIndex);
                        setSelectedAddress(cliente.direcciones[newIndex]);
                    }
                }
                // Enter: Confirmar
                if (e.key === 'Enter' && deliveryType) {
                    e.preventDefault();
                    setCurrentStep(3);
                }
                // Escape/Backspace: Volver
                if (e.key === 'Escape' || e.key === 'Backspace') {
                    e.preventDefault();
                    setCurrentStep(1);
                }
            }

            // === PASO 3: Productos ===
            if (currentStep === 3) {
                // Ctrl+H: Historial de producto-cliente
                if (e.ctrlKey && (e.key === 'h' || e.key === 'H')) {
                    e.preventDefault();
                    if (cliente && selectedCartItemId) {
                        const item = cart.find(i => i.id === selectedCartItemId);
                        if (item) {
                            handleAbrirHistorial(item);
                        }
                    } else if (!cliente) {
                        showToast('Selecciona un cliente primero', 'warning');
                    } else {
                        showToast('Selecciona un producto del carrito', 'warning');
                    }
                }

                // F1: Focus en búsqueda
                if (e.key === 'F1') {
                    e.preventDefault();
                    searchInputRef.current?.focus();
                }

                // F2: Cambiar cliente
                if (e.key === 'F2') {
                    e.preventDefault();
                    setCurrentStep(1);
                }

                // F3: Cambiar entre pickup y domicilio
                if (e.key === 'F3') {
                    e.preventDefault();
                    handleToggleDeliveryType();
                }

                // F4: Selector de precios / Doble F4: Precio libre
                if (e.key === 'F4' && selectedCartItemId) {
                    const item = cart.find(i => i.id === selectedCartItemId);
                    if (item && item.precios) {
                        e.preventDefault();
                        const now = Date.now();
                        const timeSinceLastF4 = now - lastF4PressRef.current;

                        // Doble F4 (dentro de 500ms) = Precio libre
                        if (timeSinceLastF4 < 500 && priceSelector) {
                            setPriceSelector(null);
                            lastF4PressRef.current = 0;
                            // Abrir precio libre
                            const precioCosto = item.precios?.[campoCosto] || item.precios?.precio_compra || 0;
                            setPrecioLibreData({
                                precio: '',
                                itemId: item.id,
                                precioCosto: precioCosto,
                                productoNombre: item.producto_nombre || item.nombre
                            });
                            // Verificar si tiene permiso
                            if (hasPermission('ventas_precio_libre')) {
                                setShowPrecioLibreModal(true);
                                setTimeout(() => {
                                    precioLibreInputRef.current?.focus();
                                    precioLibreInputRef.current?.select();
                                }, 100);
                            } else {
                                setShowPasswordPrecioLibreModal(true);
                                setTimeout(() => passwordPrecioLibreInputRef.current?.focus(), 100);
                            }
                        } else if (!priceSelector) {
                            // Primer F4 = Selector de precios normal
                            lastF4PressRef.current = now;
                            setPriceSelector({
                                productId: item.id,
                                currentPrice: item.precio_unitario,
                                precios: item.precios,
                                position: null
                            });
                        } else {
                            // F4 con selector abierto pero fuera de tiempo
                            lastF4PressRef.current = now;
                        }
                    }
                }

                // F5: Pausar pedido
                if (e.key === 'F5') {
                    e.preventDefault();
                    handlePausar();
                }

                // F10 o End: Procesar venta
                if ((e.key === 'End' || e.key === 'F10') && cart.length > 0 && !loading) {
                    e.preventDefault();
                    handleProcessSale();
                }

                // F11: Cambiar presentación
                if (e.key === 'F11' && selectedCartItemId) {
                    const item = cart.find(i => i.id === selectedCartItemId);
                    if (item && item.equivalencias_disponibles && item.equivalencias_disponibles.length > 1) {
                        e.preventDefault();
                        const currentEqIndex = item.equivalencias_disponibles.findIndex(eq => eq.producto_id === item.producto_id);
                        const nextIndex = (currentEqIndex + 1) % item.equivalencias_disponibles.length;
                        const nuevaPresentacion = item.equivalencias_disponibles[nextIndex];
                        cambiarPresentacion(item.id, nuevaPresentacion);
                        // Mantener selección después de cambiar presentación
                        setSelectedCartItemId(nuevaPresentacion.producto_id);
                    }
                }

                // Navegación de resultados de búsqueda
                if (showResults && searchResults.length > 0) {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setSelectedSearchResultIndex(prev =>
                            Math.min(prev + 1, searchResults.length - 1)
                        );
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setSelectedSearchResultIndex(prev => Math.max(prev - 1, 0));
                    }
                    if (e.key === 'Enter' && searchResults[selectedSearchResultIndex]) {
                        e.preventDefault();
                        handleSelectProduct(searchResults[selectedSearchResultIndex], false);
                        setSearchQuery('');
                        setShowResults(false);
                        setSelectedSearchResultIndex(0);
                    }
                }
                // Navegación del carrito cuando no hay búsqueda activa
                else if (cart.length > 0 && !showResults && !priceSelector) {
                    // Flechas: Navegar items del carrito
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const currentIndex = cart.findIndex(item => item.id === selectedCartItemId);
                        if (currentIndex === -1) {
                            setSelectedCartItemId(cart[0].id);
                        } else if (currentIndex < cart.length - 1) {
                            setSelectedCartItemId(cart[currentIndex + 1].id);
                        }
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const currentIndex = cart.findIndex(item => item.id === selectedCartItemId);
                        if (currentIndex === -1) {
                            setSelectedCartItemId(cart[cart.length - 1].id);
                        } else if (currentIndex > 0) {
                            setSelectedCartItemId(cart[currentIndex - 1].id);
                        }
                    }

                    // Delete/Supr: Eliminar item seleccionado
                    if ((e.key === 'Delete' || e.key === 'Supr') && selectedCartItemId) {
                        e.preventDefault();
                        const currentIndex = cart.findIndex(item => item.id === selectedCartItemId);
                        deleteFromCart(selectedCartItemId);
                        // Seleccionar siguiente o anterior
                        if (cart.length > 1) {
                            if (currentIndex < cart.length - 1) {
                                setSelectedCartItemId(cart[currentIndex + 1]?.id || cart[currentIndex - 1]?.id);
                            } else {
                                setSelectedCartItemId(cart[currentIndex - 1]?.id || null);
                            }
                        } else {
                            setSelectedCartItemId(null);
                        }
                    }

                    // +: Aumentar cantidad
                    if ((e.key === '+' || e.key === 'Add') && selectedCartItemId) {
                        e.preventDefault();
                        const item = cart.find(i => i.id === selectedCartItemId);
                        if (item) {
                            updateQuantity(selectedCartItemId, parseFloat(item.cantidad) + 1);
                        }
                    }

                    // -: Disminuir cantidad
                    if ((e.key === '-' || e.key === 'Subtract') && selectedCartItemId) {
                        e.preventDefault();
                        const item = cart.find(i => i.id === selectedCartItemId);
                        if (item) {
                            const newQty = parseFloat(item.cantidad) - 1;
                            if (newQty > 0) {
                                updateQuantity(selectedCartItemId, newQty);
                            } else {
                                removeFromCart(selectedCartItemId);
                            }
                        }
                    }
                }

                // Escape: Cerrar selector de direcciones, precios o deseleccionar item
                if (e.key === 'Escape') {
                    if (showAddressSelector) {
                        e.preventDefault();
                        setShowAddressSelector(false);
                    } else if (priceSelector) {
                        e.preventDefault();
                        setPriceSelector(null);
                    } else if (selectedCartItemId && !searchQuery) {
                        e.preventDefault();
                        setSelectedCartItemId(null);
                    } else if (!searchQuery && !selectedCartItemId && cart.length === 0) {
                        e.preventDefault();
                        setCurrentStep(2);
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown, true); // capture phase para ejecutar antes que Modal
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [
        currentStep, deliveryType, cliente, showResults, searchResults,
        selectedSearchResultIndex, selectedAddressIndex, searchQuery, cart,
        selectedAddress, showClientResults, clientSearchResults,
        selectedClientResultIndex, quantityModalData,
        showPaymentModal, priceSelector, loading, selectedCartItemId,
        showAddressSelector,
        deleteFromCart, updateQuantity, cambiarPresentacion, onClose, showToast,
        setSelectedCartItemId, removeFromCart
    ]);

    // Navegación por teclado del selector de precios
    useEffect(() => {
        if (!priceSelector) return;

        const handlePriceSelectorKeyDown = (e) => {
            const precios = priceSelector.precios;
            const preciosDisponibles = [];
            if (precios.precio_lista > 0) preciosDisponibles.push({ tipo: 'precio_lista', valor: precios.precio_lista });
            if (precios.precio1 > 0) preciosDisponibles.push({ tipo: 'precio1', valor: precios.precio1 });
            if (precios.precio2 > 0) preciosDisponibles.push({ tipo: 'precio2', valor: precios.precio2 });
            if (precios.precio3 > 0) preciosDisponibles.push({ tipo: 'precio3', valor: precios.precio3 });
            if (precios.precio4 > 0) preciosDisponibles.push({ tipo: 'precio4', valor: precios.precio4 });
            if (precios.precio5 > 0) preciosDisponibles.push({ tipo: 'precio5', valor: precios.precio5 });
            if (precios.precio6 > 0) preciosDisponibles.push({ tipo: 'precio6', valor: precios.precio6 });

            // F4: Doble F4 para precio libre
            if (e.key === 'F4') {
                e.preventDefault();
                const now = Date.now();
                const timeSinceLastF4 = now - lastF4PressRef.current;

                if (timeSinceLastF4 < 500) {
                    // Doble F4 - Abrir precio libre
                    const item = cart.find(i => i.id === priceSelector.productId);
                    if (item) {
                        setPriceSelector(null);
                        lastF4PressRef.current = 0;
                        const precioCosto = item.precios?.[campoCosto] || item.precios?.precio_compra || 0;
                        setPrecioLibreData({
                            precio: '',
                            itemId: item.id,
                            precioCosto: precioCosto,
                            productoNombre: item.producto_nombre || item.nombre
                        });
                        if (hasPermission('ventas_precio_libre')) {
                            setShowPrecioLibreModal(true);
                            setTimeout(() => {
                                precioLibreInputRef.current?.focus();
                                precioLibreInputRef.current?.select();
                            }, 150);
                        } else {
                            setShowPasswordPrecioLibreModal(true);
                            setTimeout(() => {
                                passwordPrecioLibreInputRef.current?.focus();
                                passwordPrecioLibreInputRef.current?.select();
                            }, 150);
                        }
                    }
                } else {
                    lastF4PressRef.current = now;
                }
                return;
            }

            if (preciosDisponibles.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedPriceIndex(prev => Math.min(prev + 1, preciosDisponibles.length - 1));
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedPriceIndex(prev => Math.max(prev - 1, 0));
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                const precioSeleccionado = preciosDisponibles[selectedPriceIndex];
                if (precioSeleccionado) {
                    changePriceLevel(priceSelector.productId, precioSeleccionado.valor, precioSeleccionado.tipo);
                    setPriceSelector(null);
                }
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setPriceSelector(null);
            }
        };

        window.addEventListener('keydown', handlePriceSelectorKeyDown, true); // capture phase
        return () => window.removeEventListener('keydown', handlePriceSelectorKeyDown, true);
    }, [priceSelector, selectedPriceIndex, changePriceLevel, cart, hasPermission]);

    // Reset selectedPriceIndex when price selector opens
    useEffect(() => {
        if (priceSelector) {
            setSelectedPriceIndex(0);
        }
    }, [priceSelector?.productId]);

    // Auto-focus en búsqueda cuando se cierran modales (como en CounterSalesPageRefactored)
    useEffect(() => {
        if (currentStep === 3 && !priceSelector && !quantityModalData && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [currentStep, priceSelector, quantityModalData]);

    // Scroll al item seleccionado en resultados de búsqueda
    useEffect(() => {
        if (showResults && searchResultsContainerRef.current && searchResults.length > 0) {
            const container = searchResultsContainerRef.current;
            const selectedElement = container.querySelector('.search-result-item.keyboard-focused');
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [selectedSearchResultIndex, showResults, searchResults.length]);

    // Scroll al item seleccionado en resultados de clientes
    useEffect(() => {
        if (showClientResults && clientResultsContainerRef.current && clientSearchResults.length > 0) {
            const container = clientResultsContainerRef.current;
            const selectedElement = container.querySelector('.search-result-item.keyboard-focused');
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [selectedClientResultIndex, showClientResults, clientSearchResults.length]);

    // Seleccionar dirección principal automáticamente
    useEffect(() => {
        if (currentStep === 2 && deliveryType === 'domicilio' && cliente?.direcciones?.length > 0) {
            if (!selectedAddress) {
                const principalAddress = cliente.direcciones.find(dir => dir.es_principal);
                const addressToSelect = principalAddress || cliente.direcciones[0];
                const indexToSelect = cliente.direcciones.indexOf(addressToSelect);
                setSelectedAddress(addressToSelect);
                setSelectedAddressIndex(indexToSelect);
            }
        }
    }, [currentStep, deliveryType, cliente, selectedAddress]);

    // Buscar cliente por teléfono o nombre
    const handleClientSearch = async () => {
        if (!phoneNumber || phoneNumber.trim().length < 2) {
            showToast('Ingrese al menos 2 caracteres para buscar', 'warning');
            return;
        }

        setPhoneSearching(true);
        try {
            // Si tiene letras, buscar por nombre
            if (/[a-zA-Z]/.test(phoneNumber)) {
                const result = await apiService.buscarClientes(phoneNumber.trim());
                if (result.success && result.data?.length > 0) {
                    setClientSearchResults(result.data);
                    setShowClientResults(true);
                } else {
                    showToast('No se encontraron clientes', 'info');
                    setShowQuickRegister(true);
                    setQuickRegisterData(prev => ({
                        ...prev,
                        nombre: phoneNumber,
                        telefonos: [{ telefono: '', tipo: 'principal' }]
                    }));
                }
            } else {
                // Buscar por teléfono
                const result = await apiService.buscarClientePorTelefono(phoneNumber.trim());
                if (result.success) {
                    setCliente(result.data);
                    // showToast('Cliente encontrado: ' + result.data.nombre, 'success');
                    setCurrentStep(2);
                } else {
                    // Cliente no encontrado - mostrar registro rápido
                    setShowQuickRegister(true);
                    setQuickRegisterData(prev => ({
                        ...prev,
                        telefonos: [{ telefono: phoneNumber, tipo: 'principal' }]
                    }));
                }
            }
        } catch (error) {
            showToast('Error al buscar cliente', 'error');
        } finally {
            setPhoneSearching(false);
        }
    };

    // Buscar clientes por nombre
    const searchClientsByName = async (name) => {
        if (name.length < 2) return;
        try {
            const result = await apiService.buscarClientes(name);
            if (result.success && result.data?.length > 0) {
                setClientSearchResults(result.data);
                setShowClientResults(true);
            } else {
                setClientSearchResults([]);
                setShowClientResults(false);
            }
        } catch (error) {
            console.error('Error buscando clientes:', error);
        }
    };

    // Seleccionar cliente de resultados
    const selectClientFromResults = (client) => {
        setCliente(client);
        setShowClientResults(false);
        setClientSearchResults([]);
        // showToast('Cliente seleccionado: ' + client.nombre, 'success');
        setCurrentStep(2);
    };

    // Buscar clientes existentes en registro rápido
    const searchQuickRegisterClients = async (searchTerm) => {
        if (searchTerm.length < 2) {
            setQuickRegisterSearchResults([]);
            setShowQuickRegisterResults(false);
            return;
        }
        try {
            const result = await apiService.buscarClientes(searchTerm);
            if (result.success && result.data?.length > 0) {
                setQuickRegisterSearchResults(result.data);
                setShowQuickRegisterResults(true);
            } else {
                setQuickRegisterSearchResults([]);
                setShowQuickRegisterResults(false);
            }
        } catch (error) {
            console.error('Error buscando clientes:', error);
        }
    };

    // Seleccionar cliente existente desde registro rápido
    const selectFromQuickRegister = (client) => {
        setCliente(client);
        setShowQuickRegister(false);
        setQuickRegisterSearchResults([]);
        setShowQuickRegisterResults(false);
        // showToast('Cliente seleccionado: ' + client.nombre, 'success');
        setCurrentStep(2);
    };

    // Manejar registro rápido de cliente
    const handleQuickRegister = async () => {
        if (!quickRegisterData.nombre.trim()) {
            showToast('El nombre es obligatorio', 'warning');
            return;
        }

        const telefonosValidos = quickRegisterData.telefonos.filter(t => t.telefono.trim().length >= 10);
        if (telefonosValidos.length === 0) {
            showToast('Debe agregar al menos un teléfono válido (10 dígitos)', 'warning');
            return;
        }

        setPhoneSearching(true);
        try {
            const clienteData = {
                nombre: quickRegisterData.nombre.trim(),
                nombre_comercial: quickRegisterData.nombre_comercial.trim() || null,
                precio_venta: quickRegisterData.precio_venta,
                telefonos: telefonosValidos,
                direcciones: quickRegisterData.direcciones.filter(d => d.direccion.trim())
            };

            const result = await apiService.crearCliente(clienteData);

            if (result.success) {
                setCliente(result.data);
                setShowQuickRegister(false);
                // showToast('Cliente registrado exitosamente', 'success');
                setCurrentStep(2);
            } else {
                showToast(result.message || 'Error al registrar cliente', 'error');
            }
        } catch (error) {
            showToast('Error al registrar cliente', 'error');
        } finally {
            setPhoneSearching(false);
        }
    };

    // Cancelar registro rápido
    const handleCancelQuickRegister = () => {
        setShowQuickRegister(false);
        setQuickRegisterSearchResults([]);
        setShowQuickRegisterResults(false);
        setCliente(null);
        setCurrentStep(2);
    };

    // Funciones para teléfonos en registro rápido
    const handleAddTelefono = () => {
        setQuickRegisterData(prev => ({
            ...prev,
            telefonos: [...prev.telefonos, { telefono: '', tipo: 'secundario', isNew: true }]
        }));
    };

    const handleRemoveTelefono = (index) => {
        setQuickRegisterData(prev => ({
            ...prev,
            telefonos: prev.telefonos.filter((_, i) => i !== index)
        }));
    };

    const handleTelefonoChange = (index, field, value) => {
        setQuickRegisterData(prev => ({
            ...prev,
            telefonos: prev.telefonos.map((tel, i) =>
                i === index ? { ...tel, [field]: value } : tel
            )
        }));
    };

    // Funciones para direcciones en registro rápido
    const handleAddDireccion = () => {
        setQuickRegisterData(prev => ({
            ...prev,
            direcciones: [...prev.direcciones, { direccion: '', latitud: null, longitud: null, es_principal: false }]
        }));
    };

    const handleRemoveDireccion = (index) => {
        setQuickRegisterData(prev => ({
            ...prev,
            direcciones: prev.direcciones.filter((_, i) => i !== index)
        }));
    };

    const handleDireccionChange = (index, field, value) => {
        setQuickRegisterData(prev => ({
            ...prev,
            direcciones: prev.direcciones.map((dir, i) =>
                i === index ? { ...dir, [field]: value } : dir
            )
        }));
    };

    // Google Maps Autocomplete handlers
    const onAutocompleteLoad = (index, autocomplete) => {
        autocompleteInstances.current[index] = autocomplete;
    };

    const handlePlaceSelect = (index) => {
        const autocomplete = autocompleteInstances.current[index];
        if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place.formatted_address) {
                setQuickRegisterData(prev => ({
                    ...prev,
                    direcciones: prev.direcciones.map((dir, i) =>
                        i === index ? {
                            ...dir,
                            direccion: place.formatted_address,
                            latitud: place.geometry?.location?.lat() || null,
                            longitud: place.geometry?.location?.lng() || null
                        } : dir
                    )
                }));
            }
        }
    };

    // Manejar selección de producto
    const handleSelectProduct = async (producto, esCodigoBarras) => {
        // Si es unidad de peso (kilo/gramo), siempre abrir modal para capturar cantidad con decimales
        const requiereModalPeso = esUnidadPeso(producto.unidad_medida);

        if ((esCodigoBarras || !preguntarCantidad) && !requiereModalPeso) {
            await addToCartDirectly(producto);
            searchInputRef.current?.focus();
        } else {
            setLoadingEquivalencias(true);
            const equivalencias = await loadEquivalencias(producto);
            setEquivalenciasDisponibles(equivalencias);
            setLoadingEquivalencias(false);

            setQuantityModalData({
                producto,
                cantidad: requiereModalPeso ? '' : 1  // Input vacío para peso para facilitar captura
            });
        }
    };

    // Manejar código de barras
    const handleBarcodeDetected = async (barcode) => {
        try {
            const result = await apiService.buscarProductos(barcode);
            if (result.success && result.data?.length > 0) {
                await addToCartDirectly(result.data[0]);
            }
        } catch (error) {
            console.error('Error buscando por código de barras:', error);
        }
        searchInputRef.current?.focus();
    };

    // Confirmar cantidad
    const handleConfirmQuantity = ({ cantidad, calculatedPricing, presentacionSeleccionada, equivalencias }) => {
        if (quantityModalData) {
            // Usar la presentación seleccionada si existe, sino el producto original
            let productoAgregar = quantityModalData.producto;

            if (presentacionSeleccionada) {
                // Mapear la presentación para tener la estructura correcta
                productoAgregar = {
                    ...presentacionSeleccionada,
                    id: presentacionSeleccionada.producto_id,
                    nombre: presentacionSeleccionada.nombre || quantityModalData.producto.nombre
                };
            }

            addToCartWithQuantity(
                productoAgregar,
                cantidad,
                calculatedPricing,
                equivalencias
            );
        }
        setQuantityModalData(null);
        setEquivalenciasDisponibles([]);
        searchInputRef.current?.focus();
    };

    // Click en precio (toggle - si ya está abierto para el mismo producto, cerrar)
    const handlePriceClick = (item, event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        // Si ya está abierto para este producto, cerrar
        if (priceSelector && priceSelector.productId === item.id) {
            setPriceSelector(null);
            return;
        }

        if (item.precios) {
            const rect = event.currentTarget.getBoundingClientRect();
            setPriceSelector({
                productId: item.id,
                currentPrice: item.precio_unitario,
                precios: item.precios,
                position: {
                    top: rect.bottom + 4,
                    left: rect.left
                }
            });
        }
    };

    // Procesar venta
    const handleProcessSale = () => {
        if (cart.length === 0) {
            showToast('Debe agregar al menos un producto', 'warning');
            return;
        }
        setShowPaymentModal(true);
    };

    // Confirmar pago
    const handleConfirmPayment = async ({ pagosDetalle, estatusPago: estatus, totalFinal, aplicaComision }) => {
        setShowPaymentModal(false);

        try {
            setLoading(true);

            const userId = user.id || user.usuario_id || user.user_id;
            const userName = user.usuario || user.nombre || user.username;

            let metodoPagoFinal = null;
            let pagosDetalleFinal = null;

            if (pagosDetalle.length === 1) {
                metodoPagoFinal = pagosDetalle[0].metodo;
            } else if (pagosDetalle.length > 1) {
                metodoPagoFinal = 'mixto';
                pagosDetalleFinal = pagosDetalle;
            }

            // Dirección de entrega
            let direccionEntrega = '';
            if (deliveryType === 'domicilio' && selectedAddress) {
                direccionEntrega = selectedAddress.direccion_completa || selectedAddress.direccion || '';
            }

            const ventaData = {
                cliente_id: cliente?.id || null,
                cliente_nombre: cliente?.nombre || 'Cliente General',
                cliente_telefono: cliente?.telefonos?.[0]?.telefono || '',
                usuario_id: userId,
                usuario_nombre: userName,
                nombre_pc: localStorage.getItem('pos_nombre_pc') || null,
                metodo_pago: metodoPagoFinal,
                pagos_detalle: pagosDetalleFinal,
                estatus_pago: estatus,
                tipo_entrega: deliveryType || 'pickup',
                direccion_entrega: direccionEntrega,
                observaciones: observaciones,
                aplica_comision: aplicaComision ? 1 : 0,
                comision_porcentaje: aplicaComision ? comisionTarjeta : 0,
                comision_monto: aplicaComision ? totals.montoComision : 0,
                recargo_domicilio_porcentaje: comisionEnvioSeleccionada?.valor || 0,
                productos: cart.map(item => {
                    const factorRecargo = 1 + (comisionEnvioSeleccionada?.valor || 0) / 100;
                    return {
                        producto_id: item.producto_id,
                        producto_nombre: item.producto_nombre || item.nombre,
                        unidad_medida: item.unidad_medida || 'PZ',
                        cantidad: item.cantidad,
                        precio_unitario: item.precio_unitario * factorRecargo,
                        precio_unitario_original: item.precio_unitario,
                        descuento: item.descuento || 0,
                        impuesto: item.impuesto || 0,
                        precio_tipo: item.precio_tipo || 'precio_lista',
                        impresora: item.impresora || null
                    };
                })
            };

            const result = await apiService.crearVenta(ventaData);

            if (result.success) {
                const ventaConFolio = {
                    ...ventaData,
                    folio: result.data.folio
                };

                if (imprimirPedido) {
                    // Imprimir directo sin diálogo
                    await imprimirPedidoDirecto(ventaConFolio);
                }

                if (onSaleCreated) {
                    onSaleCreated(pedidoInicial?.id);
                }
                onClose();
            } else {
                showToast(result.message || 'Error al crear venta', 'error');
            }
        } catch (error) {
            console.error('Error creating sale:', error);
            showToast('Error al crear venta', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Imprimir pedido directamente sin diálogo
    const imprimirPedidoDirecto = async (ventaData) => {
        try {
            // Resolver impresora: pedidos > predeterminada > sistema
            let printerName = impresoraPedidos || impresoraPredeterminada || '';

            if (!printerName && electronService.isElectronApp()) {
                try {
                    const result = await electronService.invoke('get-default-printer');
                    if (result?.success && result?.printer) {
                        printerName = result.printer.name;
                    }
                } catch (e) { }
            }
            if (!printerName) printerName = 'Microsoft Print to PDF';

            const esTicket = tipoImpresionPedido === 'ticket';
            const htmlContent = esTicket
                ? generarTicketPedidoHTML(ventaData)
                : generarFormatoPedidoHTML(ventaData);

            if (electronService.isElectronApp()) {
                const printOptions = esTicket
                    ? { pageSize: 'A4', landscape: false, margins: { marginType: 'none' } }
                    : { pageSize: 'Letter', landscape: false, margins: { marginType: 'default' } };

                await electronService.printDocument(printerName, htmlContent, printOptions);
            } else {
                const printWindow = window.open('', '_blank');
                printWindow.document.write(htmlContent);
                printWindow.document.close();
                printWindow.focus();
                printWindow.print();
                printWindow.close();
            }
        } catch (error) {
            console.error('Error al imprimir pedido:', error);
        }
    };

    // Ajustar texto a ancho máximo de caracteres
    const wrapText = (text, maxWidth) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';
        words.forEach(word => {
            if ((currentLine + ' ' + word).trim().length > maxWidth) {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            } else {
                currentLine = currentLine ? currentLine + ' ' + word : word;
            }
        });
        if (currentLine) lines.push(currentLine);
        return lines;
    };

    // Generar texto pre-formateado del pedido (estilo ticket 80mm, misma estructura que mostrador)
    const generarTicketPedidoTexto = (venta) => {
        const fecha = new Date();
        const fechaStr = fecha.toLocaleDateString('es-MX');
        const horaStr = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        const LINE = '====================================';
        const MAX_CH = 32;
        const total = venta.productos.reduce((sum, item) => sum + (item.cantidad * item.precio_unitario), 0);

        let texto = '';
        texto += `        PEDIDO #${venta.folio || 'Pendiente'}\n`;
        texto += `${LINE}\n`;
        texto += `Fecha: ${fechaStr}  Hora: ${horaStr}\n`;
        texto += `Cliente: ${venta.cliente_nombre || 'General'}\n`;
        texto += `Entrega: ${venta.tipo_entrega === 'domicilio' ? 'Domicilio' : 'Recoger en tienda'}\n`;
        if (venta.direccion_entrega) {
            const dirLines = wrapText(venta.direccion_entrega, MAX_CH - 5);
            texto += `Dir: ${dirLines[0]}\n`;
            for (let i = 1; i < dirLines.length; i++) {
                texto += `     ${dirLines[i]}\n`;
            }
        }
        if (venta.metodo_pago) {
            texto += `Pago: ${venta.metodo_pago === 'credito' ? '*** CREDITO ***' : venta.metodo_pago}\n`;
        }
        if (venta.estatus_pago !== 'pagado') {
            texto += `*** PENDIENTE DE PAGO ***\n`;
        }
        texto += `${LINE}\n`;
        texto += `CANT  DESCRIPCION          IMPORTE\n`;
        texto += `${LINE}\n`;

        venta.productos.forEach(item => {
            const subtotal = item.cantidad * item.precio_unitario;
            const cant = String(item.cantidad).padEnd(6);
            const nombre = (item.producto_nombre || '').substring(0, 20).padEnd(20);
            const imp = formatMoney(subtotal).padStart(8);
            texto += `${cant}${nombre}${imp}\n`;
            const um = item.unidad_medida || 'PZ';
            const pu = formatMoney(item.precio_unitario);
            //texto += `      ${um} x ${pu}\n`;
        });

        const ticketDesgloseIva = configImpresion.impresion_desglose_iva === true || configImpresion.impresion_desglose_iva === 'true';
        if (ticketDesgloseIva) {
            const subtSinIva = total / 1.16;
            const iva = total - subtSinIva;
            texto += `Subtotal:        ${formatMoney(subtSinIva).padStart(16)}\n`;
            texto += `IVA (16%):       ${formatMoney(iva).padStart(16)}\n`;
        }
        texto += `${LINE}\n`;
        texto += `TOTAL:           ${formatMoney(total).padStart(16)}\n`;
        texto += `${LINE}\n`;

        if (venta.observaciones) {
            texto += `Obs: ${venta.observaciones}\n`;
            texto += `${LINE}\n`;
        }

        texto += `Atendio: ${venta.usuario_nombre || user?.nombre || 'Sistema'}\n`;

        return texto;
    };

    // Generar HTML ticket (80mm) - mismas dimensiones que CounterSalesPage
    const generarTicketPedidoHTML = (venta) => {
        const ticketContent = generarTicketPedidoTexto(venta);
        const folio = venta.folio || '';

        return `<!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Pedido - ${folio}</title>
                <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128+Text&display=swap" rel="stylesheet">
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    @page { size: 80mm auto; margin: 0; }
                    body {
                        font-family: 'Courier New', Courier, monospace;
                        font-size: 12px;
                        font-weight: bold;
                        color: #000000;
                        margin: 0;
                        padding: 1mm 1mm 1mm 4mm;
                        white-space: pre-wrap;
                        background: white;
                        line-height: 1.4;
                    }
                    .barcode-container { text-align: center; margin: 5mm 0; white-space: normal; max-width: 36ch; }
                    .barcode { font-family: 'Libre Barcode 128 Text', cursive; font-size: 48px; color: #000000; line-height: 1; }
                    .barcode-text { font-family: 'Courier New', monospace; font-size: 10px; margin-top: 2mm; }
                    .ticket-logo { text-align: center; margin-bottom: 2mm; white-space: normal; max-width: 36ch; }
                    .ticket-logo img { display: block; margin: 0 auto; max-height: 100px; max-width: 250px; }
                </style>
            </head>
            <body>${empresaLogo ? `<div class="ticket-logo"><img src="${empresaLogo}" alt="Logo" /></div>` : ''}${configImpresion.impresion_encabezado_ticket ? `<div style="text-align:center;margin-bottom:3mm;white-space:pre-wrap;max-width:36ch;">${configImpresion.impresion_encabezado_ticket}</div>` : ''}${ticketContent}
${configImpresion.impresion_pie_ticket ? `<div style="text-align:center;margin-top:3mm;white-space:pre-wrap;max-width:36ch;">${configImpresion.impresion_pie_ticket}</div>` : ''}${configImpresion.impresion_codigo_barras !== false ? `<div class="barcode-container">
<div class="barcode">${folio}</div>
<div class="barcode-text">${folio}</div>
</div>` : ''}${configImpresion.impresion_leyenda_factura ? `<div style="text-align:center;margin-top:3mm;white-space:normal;max-width:36ch;font-size:11px;font-weight:bold;">${configImpresion.impresion_leyenda_factura}</div>` : ''}${(configImpresion.impresion_mostrar_qr_portal === true || configImpresion.impresion_mostrar_qr_portal === 'true') && configImpresion.impresion_portal_facturacion_url ? `<div style="text-align:center;margin-top:2mm;white-space:normal;max-width:36ch;"><img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(configImpresion.impresion_portal_facturacion_url)}" alt="QR" style="display:block;margin:0 auto;width:150px;height:150px;" /></div>` : ''}
            </body>
            </html>`;
    };

    // Generar HTML formato carta para pedido
    const generarFormatoPedidoHTML = (venta) => {
        const fecha = new Date().toLocaleString('es-MX', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
        const color = empresaFormatoColor;
        const total = venta.productos.reduce((sum, item) => sum + (item.cantidad * item.precio_unitario), 0);
        const opacidad = empresaFormatoMarcaAguaOpacidad / 100;
        const tamanoMarcaAgua = empresaFormatoMarcaAguaTamano;
        const mostrarFirma = empresaFormatoMostrarFirma || venta.metodo_pago === 'credito';
        const logoTamano = empresaFormatoLogoTamano;
        const infoFontSize = empresaFormatoInfoTamano;
        const clienteFontSize = empresaFormatoClienteTamano;
        const mostrarPagare = empresaFormatoPagare && venta.estatus_pago !== 'pagado';
        const pagareTextoDefault = 'POR EL PRESENTE PAGARÉ RECONOZCO(EMOS) DEBER Y ME(NOS) OBLIGO(AMOS) INCONDICIONAL A PAGAR A LA ORDEN DE {empresa} LA CANTIDAD QUE SE ORIGINA EN SATISFACCIÓN CAUSARÁ INTERESES MORATORIOS DE 6% MENSUAL. DESPUÉS DE SU VENCIMIENTO, EN CASO DE COBRO JUDICIAL PAGARÉ LOS GASTOS QUE SE ORIGINEN SOMETIÉNDOME A LOS TRIBUNALES QUE EL ACREEDOR ELIJA Y RENUNCIANDO AL FUERO DE MI ACTUAL O FUTURO DOMICILIO.';
        const pagareTexto = empresaFormatoPagareTexto || pagareTextoDefault;

        const productosHTML = venta.productos.map(item => `
            <tr>
              <td style="padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${item.codigo_corto || item.codigo_barras || item.producto_id || ''}</td>
              <td style="padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${item.producto_nombre}</td>
              <td style="text-align:center;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${item.cantidad} ${abreviarUnidad(item.unidad_medida)}</td>
              <td style="text-align:right;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${formatMoney(item.precio_unitario)}</td>
              <td style="text-align:right;padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:10px;">${formatMoney(item.cantidad * item.precio_unitario)}</td>
            </tr>`).join('');

        // Determinar copias a generar
        const copias = [];
        if (empresaFormatoCopiaOriginal) copias.push('ORIGINAL');
        if (empresaFormatoCopiaCliente) copias.push('COPIA CLIENTE');
        if (empresaFormatoCopiaMostrador) copias.push('COPIA MOSTRADOR');
        if (copias.length === 0) copias.push('ORIGINAL');

        const marcaAguaHTML = empresaFormatoMarcaAgua && empresaLogo ? `<div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);opacity:${opacidad};pointer-events:none;z-index:0;">
              <img src="${empresaLogo}" style="width:${tamanoMarcaAgua}px;height:${tamanoMarcaAgua}px;object-fit:contain;" />
            </div>` : '';

        const generarPagina = (etiqueta, esUltima) => `
            <div class="pagina-formato" style="position:relative;display:flex;flex-direction:column;min-height:100vh;page-break-inside:avoid;">
              <div style="flex:1;position:relative;z-index:1;">
                ${copias.length > 1 ? `<div style="text-align:right;font-size:8px;font-weight:700;color:${color};text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">${etiqueta}</div>` : ''}
                <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;padding-bottom:12px;border-bottom:2px solid ${color};">
                  <div style="display:flex;align-items:center;gap:12px;">
                    ${empresaFormatoMostrarLogo && empresaLogo ? `<img src="${empresaLogo}" style="max-width:${logoTamano}px;max-height:${Math.round(logoTamano * 0.6)}px;object-fit:contain;" />` : ''}
                    <div>
                      ${empresaNombre ? `<div style="font-size:14px;font-weight:700;color:${color};">${empresaNombre}</div>` : ''}
                      ${empresaFormatoInfoEmpresa ? `<div style="font-size:${infoFontSize}px;color:#6b7280;max-width:250px;margin-top:2px;">${empresaFormatoInfoEmpresa}</div>` : ''}
                    </div>
                  </div>
                  <div style="display:flex;gap:10px;">
                    <div style="border:2px solid ${color};border-radius:4px;min-width:120px;">
                      <div style="background:${color};color:white;padding:3px 10px;font-size:9px;font-weight:600;text-transform:uppercase;">Vendedor</div>
                      <div style="padding:5px 10px;font-size:11px;font-weight:500;">${venta.usuario_nombre || user?.nombre || 'Sistema'}</div>
                    </div>
                    <div style="border:2px solid ${color};border-radius:4px;min-width:100px;">
                      <div style="background:${color};color:white;padding:3px 10px;font-size:9px;font-weight:600;text-transform:uppercase;">Folio</div>
                      <div style="padding:5px 10px;font-size:11px;font-weight:500;">#${venta.folio || 'Pendiente'}</div>
                    </div>
                  </div>
                </div>
                <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:10px 14px;margin-bottom:14px;">
                  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px 16px;">
                    <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Cliente</span><div style="font-size:${clienteFontSize}px;font-weight:600;">${venta.cliente_nombre || 'Cliente General'}</div></div>
                    <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Teléfono</span><div style="font-size:${clienteFontSize}px;">${venta.cliente_telefono || ''}</div></div>
                    <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Fecha</span><div style="font-size:${clienteFontSize}px;">${fecha}</div></div>
                    ${venta.direccion_entrega ? `<div style="grid-column:span 2;"><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Domicilio</span><div style="font-size:${clienteFontSize}px;">${venta.direccion_entrega}</div></div>` : ''}
                    <div><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Condiciones de pago</span><div style="font-size:${clienteFontSize}px;">${venta.metodo_pago === 'credito' ? 'Crédito' : venta.metodo_pago === 'tarjeta' ? 'Tarjeta' : 'Efectivo'}${venta.estatus_pago !== 'pagado' ? ' - Pendiente' : ''}</div></div>
                    ${venta.observaciones ? `<div style="grid-column:span 3;"><span style="font-size:${clienteFontSize - 2}px;color:#6b7280;text-transform:uppercase;font-weight:600;">Observaciones</span><div style="font-size:${clienteFontSize}px;">${venta.observaciones}</div></div>` : ''}
                  </div>
                </div>
                <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
                  <thead>
                    <tr style="background:${color};">
                      <th style="color:white;padding:5px 6px;text-align:left;font-size:9px;text-transform:uppercase;font-weight:600;width:70px;">Clave</th>
                      <th style="color:white;padding:5px 6px;text-align:left;font-size:9px;text-transform:uppercase;font-weight:600;">Artículo</th>
                      <th style="color:white;padding:5px 6px;text-align:center;font-size:9px;text-transform:uppercase;font-weight:600;width:60px;">Unidad</th>
                      <th style="color:white;padding:5px 6px;text-align:right;font-size:9px;text-transform:uppercase;font-weight:600;width:70px;">Precio</th>
                      <th style="color:white;padding:5px 6px;text-align:right;font-size:9px;text-transform:uppercase;font-weight:600;width:80px;">Importe</th>
                    </tr>
                  </thead>
                  <tbody>${productosHTML}</tbody>
                </table>
                <div style="text-align:center;font-size:10px;font-weight:600;color:${color};margin-bottom:12px;">
                  ${venta.tipo_entrega === 'domicilio' ? 'ENTREGA A DOMICILIO' : 'RECOGER EN TIENDA'}
                  ${venta.metodo_pago ? ` &bull; ${venta.metodo_pago === 'credito' ? 'CRÉDITO' : venta.metodo_pago.toUpperCase()}` : ''}
                  ${venta.estatus_pago !== 'pagado' ? ' &bull; <span style="color:#dc2626;">PENDIENTE DE PAGO</span>' : ''}
                </div>
              </div>
              <div style="margin-top:auto;position:relative;z-index:1;">
                <div style="display:flex;justify-content:flex-end;margin-bottom:12px;">
                  <div style="border:1.5px solid #374151;min-width:220px;">
                    <div style="display:flex;border-bottom:1px solid #374151;">
                      <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                        <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Subtotal</div>
                        <div style="font-size:10px;font-weight:500;text-align:right;">${empresaFormatoDesgloseIva ? formatMoney(total / 1.16) : formatMoney(total)}</div>
                      </div>
                      <div style="flex:1;padding:4px 8px;">
                        <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">IVA ${empresaFormatoDesgloseIva ? '16%' : 'al 0%'}</div>
                        <div style="font-size:10px;font-weight:500;text-align:right;">${empresaFormatoDesgloseIva ? formatMoney(total - total / 1.16) : formatMoney(0)}</div>
                      </div>
                    </div>
                    <div style="display:flex;background:${color};">
                      <div style="flex:1;padding:6px 8px;">
                        <div style="font-size:7px;color:rgba(255,255,255,0.8);text-transform:uppercase;font-weight:600;">Total (MXN)</div>
                        <div style="font-size:14px;font-weight:700;color:white;text-align:right;">${formatMoney(total)}</div>
                      </div>
                    </div>
                  </div>
                </div>
                ${mostrarPagare ? `
                <div style="border:1.5px solid #374151;margin:12px 0;page-break-inside:avoid;">
                  <div style="display:flex;border-bottom:1px solid #374151;">
                    <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                      <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Pagaré No.</div>
                      <div style="font-size:9px;font-weight:700;">ÚNICO</div>
                    </div>
                    <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                      <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Emisión</div>
                      <div style="font-size:9px;">${fecha}</div>
                    </div>
                    <div style="flex:1;padding:4px 8px;border-right:1px solid #374151;">
                      <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Importe</div>
                      <div style="font-size:9px;font-weight:700;">${formatMoney(total)}</div>
                    </div>
                    <div style="flex:1;padding:4px 8px;">
                      <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;">Folio</div>
                      <div style="font-size:9px;font-weight:700;">#${venta.folio || 'Pendiente'}</div>
                    </div>
                  </div>
                  <div style="padding:6px 8px;border-bottom:1px solid #374151;">
                    <p style="font-size:7px;line-height:1.4;color:#374151;text-align:justify;">${pagareTexto
                      .replace(/\{ciudad\}/g, venta.direccion_entrega || '')
                      .replace(/\{fecha_emision\}/g, fecha)
                      .replace(/\{empresa\}/g, empresaNombre)
                      .replace(/\{importe\}/g, formatMoney(total))
                      .replace(/\{importe_letra\}/g, '')
                      .replace(/\{fecha_vencimiento\}/g, '')
                    }</p>
                  </div>
                  <div style="display:flex;">
                    <div style="flex:1;padding:6px 8px;border-right:1px solid #374151;">
                      <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Otorgante</div>
                      <div style="font-size:8px;">${empresaNombre}</div>
                    </div>
                    <div style="flex:1;padding:6px 8px;border-right:1px solid #374151;">
                      <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Vencimiento</div>
                      <div style="font-size:8px;"></div>
                    </div>
                    <div style="flex:1;padding:6px 8px;">
                      <div style="font-size:7px;color:#6b7280;text-transform:uppercase;font-weight:600;margin-bottom:2px;">Domicilio</div>
                      <div style="font-size:8px;">${venta.direccion_entrega || ''}</div>
                    </div>
                  </div>
                  <div style="display:flex;border-top:1px solid #374151;">
                    <div style="flex:1;padding:8px;border-right:1px solid #374151;text-align:center;">
                      <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                      <div style="font-size:7px;color:#6b7280;">Nombre</div>
                    </div>
                    <div style="flex:1;padding:8px;border-right:1px solid #374151;text-align:center;">
                      <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                      <div style="font-size:7px;color:#6b7280;">Firma</div>
                    </div>
                    <div style="flex:1;padding:8px;text-align:center;">
                      <div style="width:80%;border-bottom:1px solid #374151;margin:18px auto 4px auto;"></div>
                      <div style="font-size:7px;color:#6b7280;">Persona autorizada a recibir</div>
                    </div>
                  </div>
                </div>
                ` : ''}
                <div style="text-align:center;font-size:8px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">
                  ${empresaFormatoPiePagina ? `<p style="margin-bottom:4px;">${empresaFormatoPiePagina}</p>` : ''}
                  <p>Impreso el ${fecha} | Usuario: ${venta.usuario_nombre || user?.nombre || 'Sistema'}</p>
                </div>
              </div>
            </div>
            ${!esUltima ? '<div style="page-break-after:always;"></div>' : ''}`;

        const paginasHTML = copias.map((etiqueta, idx) => generarPagina(etiqueta, idx === copias.length - 1)).join('');

        return `<!DOCTYPE html><html><head><meta charset="UTF-8">
            <style>
            @page{size:letter;margin:0.8cm;}
            *{margin:0;padding:0;box-sizing:border-box;}
            body{font-family:'Segoe UI',Arial,sans-serif;font-size:11px;line-height:1.4;color:#1f2937;position:relative;}
            @media print { .pagina-formato { min-height:100vh !important; } }
            </style></head>
            <body>${marcaAguaHTML}${paginasHTML}</body></html>`;
    };

    // Cerrar modal de impresión y finalizar
    const handlePrintModalClose = () => {
        setShowPrintModal(false);
        setVentaParaImprimir(null);
        if (onSaleCreated) {
            onSaleCreated(pedidoInicial?.id);
        }
        onClose();
    };

    // Pausar venta
    const handlePausar = () => {
        if (cart.length === 0) {
            showToast('No hay productos para pausar', 'warning');
            return;
        }

        if (onPausar) {
            onPausar({
                id: pedidoInicial?.id || `pedido_${Date.now()}`,
                timestamp: Date.now(),
                cliente,
                cart,
                deliveryType,
                selectedAddress,
                metodoPago,
                observaciones,
                currentStep
            });
            // Cerrar el modal después de pausar
            onClose();
        }
    };

    // Toggle delivery type
    const handleToggleDeliveryType = () => {
        // Cerrar selector de direcciones si está abierto
        setShowAddressSelector(false);

        if (deliveryType === 'domicilio') {
            setDeliveryType('pickup');
            setSelectedAddress(null);
            setComisionEnvioSeleccionada(null); // Reset comisión de envío
        } else {
            setDeliveryType('domicilio');
            if (cliente?.direcciones?.length > 0) {
                const principalAddress = cliente.direcciones.find(dir => dir.es_principal);
                setSelectedAddress(principalAddress || cliente.direcciones[0]);
            }
        }
    };

    // Get price label based on customer's price type
    const getPriceLabel = () => {
        if (!cliente || !cliente.precio_venta || cliente.precio_venta === 'precio_lista') {
            return 'Precio Lista';
        }
        const priceLabels = {
            'precio1': 'Precio 1',
            'precio2': 'Precio 2',
            'precio3': 'Precio 3',
            'precio4': 'Precio 4',
            'precio5': 'Precio 5',
            'precio6': 'Precio 6'
        };
        return priceLabels[cliente.precio_venta] || 'Precio Lista';
    };

    // === FUNCIONES DE PRECIO LIBRE (doble F4) ===

    const handleVerificarPasswordPrecioLibre = async () => {
        if (!passwordPrecioLibre) {
            showToast('Ingresa tu contraseña', 'error');
            return;
        }

        setVerificandoPasswordPrecioLibre(true);
        try {
            const result = await apiService.verificarPermisoPrecioLibre(passwordPrecioLibre);

            if (result.success) {
                setUsuarioAutorizadorPrecioLibre(result.data?.usuario_nombre || result.data?.nombre || 'N/A');
                setShowPasswordPrecioLibreModal(false);
                setPasswordPrecioLibre('');
                setShowPrecioLibreModal(true);
                setTimeout(() => {
                    precioLibreInputRef.current?.focus();
                    precioLibreInputRef.current?.select();
                }, 100);
            } else {
                showToast(result.message || 'Acceso denegado', 'error');
            }
        } catch (error) {
            console.error('Error verificando permiso:', error);
            showToast('Error al verificar permiso', 'error');
        } finally {
            setVerificandoPasswordPrecioLibre(false);
        }
    };

    const handleAplicarPrecioLibre = () => {
        const precioNuevo = parseFloat(precioLibreData.precio);

        if (!precioNuevo || precioNuevo <= 0) {
            showToast('Ingresa un precio válido', 'error');
            return;
        }

        if (precioNuevo < precioLibreData.precioCosto) {
            showToast(`El precio no puede ser menor al costo (${formatMoney(precioLibreData.precioCosto)})`, 'error');
            return;
        }

        // Aplicar el precio libre al item del carrito
        setCart(prevCart => prevCart.map(item => {
            if (item.id === precioLibreData.itemId) {
                return {
                    ...item,
                    precio_unitario: precioNuevo,
                    precio_tipo: 'precio_libre'
                };
            }
            return item;
        }));

        showToast(`Precio libre aplicado: ${formatMoney(precioNuevo)}`, 'success');
        cerrarPrecioLibre();
    };

    const cerrarPrecioLibre = () => {
        setShowPrecioLibreModal(false);
        setShowPasswordPrecioLibreModal(false);
        setPrecioLibreData({ precio: '', itemId: null, precioCosto: 0, productoNombre: '' });
        setPasswordPrecioLibre('');
        setUsuarioAutorizadorPrecioLibre(null);
        setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    // === HISTORIAL PRODUCTO-CLIENTE (Ctrl+H) ===
    const handleAbrirHistorial = async (item) => {
        setHistorialProductoNombre(item.producto_nombre || item.nombre);
        setHistorialItemId(item.id);
        setHistorialData([]);
        setHistorialCostoActual(0);
        setHistorialCostoTipoActual('');
        setShowHistorialModal(true);
        setLoadingHistorial(true);
        try {
            const result = await apiService.getHistorialProductoCliente(item.producto_id, cliente.id);
            if (result.success) {
                setHistorialData(result.data?.historial || []);
                setHistorialCostoActual(result.data?.costo_actual || 0);
                setHistorialCostoTipoActual(result.data?.costo_tipo_actual || '');
            } else {
                showToast(result.message || 'Error al obtener historial', 'error');
            }
        } catch (error) {
            console.error('Error obteniendo historial:', error);
            showToast('Error al obtener historial', 'error');
        } finally {
            setLoadingHistorial(false);
        }
    };

    const aplicarPrecioHistorial = (precio) => {
        if (!historialItemId) return;
        setCart(prevCart => prevCart.map(item => {
            if (item.id === historialItemId) {
                return {
                    ...item,
                    precio_unitario: precio,
                    precio_tipo: 'precio_libre'
                };
            }
            return item;
        }));
        showToast(`Precio ${formatMoney(precio)} aplicado del historial`, 'success');
        setShowHistorialModal(false);
    };

    // Custom title for step 3 with client info and action buttons
    const renderModalTitle = () => {
        if (currentStep === 2) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px', fontWeight: 600 }}>Nuevo Pedido</span>
                    <span style={{ fontSize: '20px', color: '#6b7280' }}>•</span>
                    <span style={{ fontSize: '22px', fontWeight: 500, color: '#667eea' }}>Seleccione Tipo de Entrega</span>
                </div>
            );
        }
        if (currentStep === 3) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '16px' }}>
                    {/* Columna 1: Título + Precio */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                        <span style={{ fontSize: '26px', fontWeight: 600 }}>Nuevo Pedido</span>
                        <span style={{
                            padding: '6px 14px',
                            borderRadius: '6px',
                            fontSize: '16px',
                            fontWeight: 600,
                            background: !cliente || !cliente.precio_venta || cliente.precio_venta === 'precio_lista' ? '#f0fdf4' :
                                cliente.precio_venta === 'precio1' ? '#dbeafe' :
                                    cliente.precio_venta === 'precio2' ? '#d1fae5' :
                                        cliente.precio_venta === 'precio3' ? '#fef3c7' :
                                            cliente.precio_venta === 'precio4' ? '#fce7f3' :
                                                cliente.precio_venta === 'precio5' ? '#e0e7ff' :
                                                    cliente.precio_venta === 'precio6' ? '#fae8ff' : '#f0fdf4',
                            color: !cliente || !cliente.precio_venta || cliente.precio_venta === 'precio_lista' ? '#15803d' :
                                cliente.precio_venta === 'precio1' ? '#1e40af' :
                                    cliente.precio_venta === 'precio2' ? '#065f46' :
                                        cliente.precio_venta === 'precio3' ? '#92400e' :
                                            cliente.precio_venta === 'precio4' ? '#9f1239' :
                                                cliente.precio_venta === 'precio5' ? '#4338ca' :
                                                    cliente.precio_venta === 'precio6' ? '#86198f' : '#15803d',
                            border: !cliente || !cliente.precio_venta || cliente.precio_venta === 'precio_lista' ? '1px solid #86efac' :
                                cliente.precio_venta === 'precio1' ? '1px solid #93c5fd' :
                                    cliente.precio_venta === 'precio2' ? '1px solid #6ee7b7' :
                                        cliente.precio_venta === 'precio3' ? '1px solid #fcd34d' :
                                            cliente.precio_venta === 'precio4' ? '1px solid #f9a8d4' :
                                                cliente.precio_venta === 'precio5' ? '1px solid #a5b4fc' :
                                                    cliente.precio_venta === 'precio6' ? '1px solid #e879f9' : '1px solid #86efac'
                        }}>
                            {getPriceLabel()}
                        </span>
                    </div>

                    {/* Columna 2: Cliente (centrado) */}
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <div
                            className="client-info-badge"
                            onClick={() => setCurrentStep(1)}
                            title="Click para cambiar cliente"
                            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 14px', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db', cursor: 'pointer', transition: 'all 0.15s ease' }}
                        >
                            <User size={22} color="#6b7280" />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '18px' }}>{cliente ? cliente.nombre : 'Cliente General'}</span>
                                    {cliente && cliente.nombre_comercial && (
                                        <span style={{ color: '#667eea', fontSize: '16px', fontStyle: 'italic' }}>({cliente.nombre_comercial})</span>
                                    )}
                                    {deliveryType && (
                                        <span
                                            onClick={(e) => { e.stopPropagation(); handleToggleDeliveryType(); }}
                                            title="Click para cambiar tipo de entrega"
                                            style={{
                                                padding: '4px 10px',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                background: deliveryType === 'domicilio' ? '#dbeafe' : '#f3f4f6',
                                                color: deliveryType === 'domicilio' ? '#1e40af' : '#374151',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                border: deliveryType === 'domicilio' ? '1px dashed #93c5fd' : '1px dashed #d1d5db'
                                            }}>
                                            {deliveryType === 'domicilio' ? '🏠 Domicilio' : '🏪 Pickup'}
                                            <span style={{ fontSize: '11px', opacity: 0.7 }}>⇄</span>
                                        </span>
                                    )}
                                </div>
                                {deliveryType === 'domicilio' && cliente?.direcciones?.length > 0 && (
                                    <div style={{ position: 'relative' }}>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setShowAddressSelector(!showAddressSelector); }}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: '4px 8px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: '#3b82f6',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                borderRadius: '4px',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.target.style.background = '#eff6ff'}
                                            onMouseLeave={(e) => e.target.style.background = 'none'}
                                            title="Clic para cambiar dirección"
                                        >
                                            <MapPin size={14} />
                                            <span style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {selectedAddress?.direccion || 'Seleccionar dirección'}
                                            </span>
                                            <span style={{ fontSize: '10px' }}>▼</span>
                                        </button>
                                        {showAddressSelector && (
                                            <>
                                                <div
                                                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }}
                                                    onClick={() => setShowAddressSelector(false)}
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    marginTop: '4px',
                                                    background: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                    zIndex: 9999,
                                                    minWidth: '350px',
                                                    maxWidth: '500px'
                                                }}>
                                                    <div style={{
                                                        padding: '10px 14px',
                                                        borderBottom: '1px solid #e5e7eb',
                                                        background: '#f9fafb',
                                                        borderRadius: '8px 8px 0 0'
                                                    }}>
                                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                                                            Seleccionar dirección de entrega
                                                        </span>
                                                    </div>
                                                    {cliente.direcciones.map((dir, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => {
                                                                setSelectedAddress(dir);
                                                                setSelectedAddressIndex(idx);
                                                                setShowAddressSelector(false);
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px 14px',
                                                                border: 'none',
                                                                background: selectedAddress?.direccion === dir.direccion ? '#eff6ff' : 'white',
                                                                textAlign: 'left',
                                                                cursor: 'pointer',
                                                                fontSize: '14px',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                gap: '10px',
                                                                borderBottom: '1px solid #f3f4f6',
                                                                transition: 'background 0.15s'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (selectedAddress?.direccion !== dir.direccion) {
                                                                    e.target.style.background = '#f9fafb';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.target.style.background = selectedAddress?.direccion === dir.direccion ? '#eff6ff' : 'white';
                                                            }}
                                                        >
                                                            <span style={{
                                                                color: '#1f2937',
                                                                flex: 1,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                {dir.direccion || dir.direccion_completa}
                                                            </span>
                                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                                                {dir.es_principal && (
                                                                    <span style={{
                                                                        padding: '2px 6px',
                                                                        background: '#dcfce7',
                                                                        color: '#15803d',
                                                                        borderRadius: '4px',
                                                                        fontSize: '10px',
                                                                        fontWeight: 600
                                                                    }}>
                                                                        Principal
                                                                    </span>
                                                                )}
                                                                {selectedAddress?.direccion === dir.direccion && (
                                                                    <span style={{ color: '#3b82f6', fontWeight: 600 }}>✓</span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <span style={{ fontSize: '20px', color: '#9ca3af', alignSelf: 'flex-start', marginLeft: '4px' }}>✎</span>
                        </div>
                    </div>

                    {/* Columna 3: Comisión envío + Pausar (derecha) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        {deliveryType === 'domicilio' && comisionesEnvio.length > 0 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 12px',
                                background: '#fefce8',
                                borderRadius: '8px',
                                border: '1px solid #fde047'
                            }}>
                                <Truck size={18} color="#ca8a04" />
                                <span style={{ fontSize: '13px', color: '#854d0e', fontWeight: 500 }}>Envío:</span>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                        onClick={() => setComisionEnvioSeleccionada(null)}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: '4px',
                                            border: comisionEnvioSeleccionada === null ? '2px solid #ca8a04' : '1px solid #d1d5db',
                                            background: comisionEnvioSeleccionada === null ? '#fef9c3' : 'white',
                                            color: comisionEnvioSeleccionada === null ? '#854d0e' : '#6b7280',
                                            fontSize: '13px',
                                            fontWeight: comisionEnvioSeleccionada === null ? 600 : 400,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s'
                                        }}
                                    >
                                        Sin Costo
                                    </button>
                                    {comisionesEnvio.map((comision) => (
                                        <button
                                            key={comision.id}
                                            onClick={() => setComisionEnvioSeleccionada(comision)}
                                            style={{
                                                padding: '4px 10px',
                                                borderRadius: '4px',
                                                border: comisionEnvioSeleccionada?.id === comision.id ? '2px solid #ca8a04' : '1px solid #d1d5db',
                                                background: comisionEnvioSeleccionada?.id === comision.id ? '#fef9c3' : 'white',
                                                color: comisionEnvioSeleccionada?.id === comision.id ? '#854d0e' : '#374151',
                                                fontSize: '13px',
                                                fontWeight: comisionEnvioSeleccionada?.id === comision.id ? 600 : 400,
                                                cursor: 'pointer',
                                                transition: 'all 0.15s'
                                            }}
                                        >
                                            {comision.valor}%
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {onPausar && (
                            <Button
                                variant="outline"
                                size="small"
                                onClick={handlePausar}
                                disabled={cart.length === 0}
                                style={{
                                    background: cart.length > 0 ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : undefined,
                                    color: cart.length > 0 ? 'white' : undefined,
                                    border: cart.length > 0 ? 'none' : undefined
                                }}
                            >
                                Pausar (F5)
                            </Button>
                        )}
                    </div>
                </div>
            );
        }
        return 'Nuevo Pedido';
    };

    // Renderizar paso actual
    const renderStep = () => {
        switch (currentStep) {
            case 1:
                // Formulario de registro rápido
                if (showQuickRegister) {
                    return (
                        <div className="wizard-step phone-step">
                            <div className="step-header">
                                <h2>{quickRegisterData.cliente_id ? 'Actualizar Cliente' : 'Registrar Nuevo Cliente'}</h2>
                                <p>
                                    {quickRegisterData.cliente_id
                                        ? 'Modifica los datos del cliente'
                                        : `Complete los datos para registrar un nuevo cliente`
                                    }
                                </p>
                            </div>

                            <div className="step-content">
                                <div className="quick-register-form">
                                    <div className="quick-register-row-top">
                                        <div className="form-group" style={{ position: 'relative', flex: 1 }}>
                                            <label htmlFor="nombre">Nombre del Cliente *</label>
                                            <Input
                                                id="nombre"
                                                ref={quickRegisterNameRef}
                                                placeholder="Nombre completo"
                                                value={quickRegisterData.nombre}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setQuickRegisterData(prev => ({ ...prev, nombre: value }));
                                                    searchQuickRegisterClients(value);
                                                }}
                                                onKeyPress={(e) => e.key === 'Enter' && handleQuickRegister()}
                                            />
                                            {showQuickRegisterResults && quickRegisterSearchResults.length > 0 && (
                                                <div className="search-results-dropdown" style={{ marginTop: '4px' }}>
                                                    <div className="search-hint" style={{ background: '#fef3c7', borderBottom: '1px solid #fcd34d', color: '#92400e' }}>
                                                        Clientes existentes con nombre similar
                                                    </div>
                                                    {quickRegisterSearchResults.map((client) => (
                                                        <div
                                                            key={client.id}
                                                            className="search-result-item"
                                                            onClick={() => selectFromQuickRegister(client)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="result-info">
                                                                <span className="result-name">{client.nombre}</span>
                                                                {client.telefonos?.length > 0 && (
                                                                    <span className="result-code">
                                                                        {client.telefonos.map(t => t.telefono || t).join(', ')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="result-price" style={{ fontSize: '13px', color: '#16a34a' }}>
                                                                Usar existente
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label htmlFor="nombre_comercial">Nombre Comercial (Opcional)</label>
                                            <Input
                                                id="nombre_comercial"
                                                placeholder="Nombre del negocio"
                                                value={quickRegisterData.nombre_comercial}
                                                onChange={(e) => setQuickRegisterData(prev => ({ ...prev, nombre_comercial: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="quick-register-row-bottom">
                                        <div className="form-group">
                                            <div className="direcciones-header">
                                                <label>Telefonos *</label>
                                                <Button variant="ghost" size="small" onClick={handleAddTelefono} type="button">
                                                    <Plus size={14} /> Agregar
                                                </Button>
                                            </div>
                                            <div className="direcciones-list">
                                                {quickRegisterData.telefonos.map((telefono, index) => (
                                                    <div key={index} className="direccion-item">
                                                        <div className="direccion-field">
                                                            <Input
                                                                placeholder={`Telefono ${index + 1} (10 digitos)`}
                                                                type="text"
                                                                value={telefono.telefono}
                                                                onChange={(e) => handleTelefonoChange(index, 'telefono', e.target.value)}
                                                                maxLength={10}
                                                            />
                                                        </div>
                                                        <div className="direccion-actions">
                                                            <label className="principal-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={telefono.tipo === 'principal'}
                                                                    onChange={(e) => {
                                                                        const newTelefonos = quickRegisterData.telefonos.map((tel, i) => ({
                                                                            ...tel,
                                                                            tipo: i === index ? (e.target.checked ? 'principal' : 'secundario') : (e.target.checked ? 'secundario' : tel.tipo)
                                                                        }));
                                                                        setQuickRegisterData(prev => ({ ...prev, telefonos: newTelefonos }));
                                                                    }}
                                                                />
                                                                {telefono.tipo === 'principal' ? (
                                                                    <span className="principal-badge">Principal</span>
                                                                ) : (
                                                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Principal</span>
                                                                )}
                                                            </label>
                                                            {quickRegisterData.telefonos.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="remove-direccion-btn"
                                                                    onClick={() => handleRemoveTelefono(index)}
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <div className="direcciones-header">
                                                <label>Direcciones (Opcional)</label>
                                                <Button variant="ghost" size="small" onClick={handleAddDireccion} type="button">
                                                    <Plus size={14} /> Agregar
                                                </Button>
                                            </div>
                                            <div className="direcciones-list">
                                                {quickRegisterData.direcciones.map((direccion, index) => (
                                                    <div key={index} className="direccion-item">
                                                        <div className="direccion-field">
                                                            {isGoogleMapsLoaded ? (
                                                                <Autocomplete
                                                                    onLoad={(autocomplete) => onAutocompleteLoad(index, autocomplete)}
                                                                    onPlaceChanged={() => handlePlaceSelect(index)}
                                                                    options={{
                                                                        componentRestrictions: { country: 'mx' },
                                                                        fields: ['formatted_address', 'geometry']
                                                                    }}
                                                                >
                                                                    <Input
                                                                        placeholder={`Direccion ${index + 1} - Buscar con Google Maps...`}
                                                                        value={direccion.direccion}
                                                                        onChange={(e) => handleDireccionChange(index, 'direccion', e.target.value)}
                                                                    />
                                                                </Autocomplete>
                                                            ) : (
                                                                <Input
                                                                    placeholder={`Direccion ${index + 1}`}
                                                                    value={direccion.direccion}
                                                                    onChange={(e) => handleDireccionChange(index, 'direccion', e.target.value)}
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="direccion-actions">
                                                            <label className="principal-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={direccion.es_principal}
                                                                    onChange={(e) => {
                                                                        const newDirecciones = quickRegisterData.direcciones.map((dir, i) => ({
                                                                            ...dir,
                                                                            es_principal: i === index ? e.target.checked : (e.target.checked ? false : dir.es_principal)
                                                                        }));
                                                                        setQuickRegisterData(prev => ({ ...prev, direcciones: newDirecciones }));
                                                                    }}
                                                                />
                                                                {direccion.es_principal ? (
                                                                    <span className="principal-badge">Principal</span>
                                                                ) : (
                                                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Principal</span>
                                                                )}
                                                            </label>
                                                            {direccion.latitud && direccion.longitud && (
                                                                <span className="coords-badge" title={`${direccion.latitud.toFixed(6)}, ${direccion.longitud.toFixed(6)}`}>
                                                                    📍
                                                                </span>
                                                            )}
                                                            {quickRegisterData.direcciones.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="remove-direccion-btn"
                                                                    onClick={() => handleRemoveDireccion(index)}
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="step-actions">
                                <Button variant="outline" onClick={() => {
                                    setShowQuickRegister(false);
                                    setQuickRegisterSearchResults([]);
                                    setShowQuickRegisterResults(false);
                                }} disabled={phoneSearching}>
                                    Atrás
                                </Button>
                                <Button variant="outline" onClick={handleCancelQuickRegister} disabled={phoneSearching}>
                                    Continuar sin Cliente
                                </Button>
                                <Button onClick={handleQuickRegister} disabled={phoneSearching}>
                                    {phoneSearching ? 'Registrando...' : 'Registrar Cliente'}
                                </Button>
                            </div>
                        </div>
                    );
                }

                // Vista principal de búsqueda de cliente
                return (
                    <div className="wizard-step phone-step" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div className="step-header">
                            <div style={{ textAlign: 'center' }}>
                                <h2>Identificación del Cliente</h2>
                                <p>Buscar por teléfono o nombre del cliente</p>
                            </div>
                        </div>

                        <div className="step-content" style={{ maxWidth: '500px', margin: '0 auto', width: '100%', flex: '0 0 auto' }}>
                            <div className="phone-input-group" style={{ position: 'relative' }}>
                                <Input
                                    ref={phoneInputRef}
                                    type="text"
                                    placeholder="Teléfono (10 dígitos) o Nombre del cliente..."
                                    value={phoneNumber}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setPhoneNumber(value);
                                        if (value.length >= 2 && /[a-zA-Z]/.test(value)) {
                                            searchClientsByName(value);
                                        } else {
                                            setShowClientResults(false);
                                        }
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !showClientResults) {
                                            handleClientSearch();
                                        }
                                    }}
                                    className="phone-input-large"
                                    style={{ fontSize: '18px', padding: '14px 16px' }}
                                />

                                {showClientResults && clientSearchResults.length > 0 && (
                                    <div
                                        ref={clientResultsContainerRef}
                                        className="search-results-dropdown"
                                        style={{
                                            marginTop: '4px',
                                            maxHeight: '220px',
                                            overflowY: 'auto',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                        }}
                                    >
                                        <div className="search-hint" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                            Use ↑↓ para navegar, <kbd>Enter</kbd> para seleccionar
                                        </div>
                                        {clientSearchResults.map((client, index) => (
                                            <div
                                                key={client.id}
                                                className={`search-result-item ${selectedClientResultIndex === index ? 'keyboard-focused' : ''}`}
                                                onClick={() => selectClientFromResults(client)}
                                            >
                                                <div className="result-info">
                                                    <span className="result-name">{client.nombre}</span>
                                                    {client.nombre_comercial && (
                                                        <span className="result-code">{client.nombre_comercial}</span>
                                                    )}
                                                    {client.telefonos?.length > 0 && (
                                                        <span className="result-code">
                                                            📞 {client.telefonos.map(t => t.telefono || t).join(', ')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="result-price" style={{ fontSize: '13px', color: '#667eea' }}>
                                                    Seleccionar →
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Spacer para empujar botones al fondo */}
                        <div style={{ flex: 1 }}></div>

                        <div className="step-actions" style={{ marginTop: 'auto' }}>
                            <Button variant="outline" onClick={onClose} disabled={phoneSearching}>
                                Cancelar
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setCliente(null);
                                    setPhoneNumber('');
                                    setCurrentStep(2);
                                }}
                                disabled={phoneSearching}
                            >
                                Omitir (F2)
                            </Button>
                            <Button onClick={handleClientSearch} disabled={phoneSearching || phoneNumber.length < 2}>
                                {phoneSearching ? 'Buscando...' : 'Buscar Cliente'}
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowQuickRegister(true);
                                    setQuickRegisterData({
                                        nombre: '',
                                        nombre_comercial: '',
                                        precio_venta: 'precio_lista',
                                        telefonos: [{ telefono: phoneNumber || '', tipo: 'principal' }],
                                        direcciones: [{ direccion: '', latitud: null, longitud: null, es_principal: true }]
                                    });
                                }}
                                disabled={phoneSearching}
                                style={{ background: '#16a34a', color: 'white', borderColor: '#16a34a' }}
                            >
                                <Plus size={16} style={{ marginRight: '4px' }} />
                                Nuevo Cliente
                            </Button>
                        </div>

                        <div style={{ textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '12px' }}>
                            <p style={{ margin: 0, fontSize: '11px', color: '#6b7280' }}>
                                <kbd>F2</kbd> : Continuar sin cliente | <kbd>Esc</kbd>: Cerrar
                            </p>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="wizard-step delivery-step" style={{
                        padding: '20px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        maxWidth: '700px',
                        margin: '0 auto'
                    }}>
                        {/* Header con información del cliente */}
                        <div style={{
                            background: '#f8fafc',
                            borderRadius: '10px',
                            padding: '14px 18px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: '#e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <User size={20} color="#64748b" />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
                                        {cliente?.nombre || 'Cliente General'}
                                    </h3>
                                    {cliente?.nombre_comercial && (
                                        <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                                            {cliente.nombre_comercial}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Selección de dirección */}
                        {cliente?.direcciones?.length > 0 && (
                            <div style={{
                                background: 'white',
                                borderRadius: '10px',
                                padding: '14px',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                    <MapPin size={16} color="#64748b" />
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                                        Dirección de entrega
                                    </span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {cliente.direcciones.map((dir, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => {
                                                setSelectedAddressIndex(idx);
                                                setSelectedAddress(dir);
                                            }}
                                            style={{
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                border: idx === selectedAddressIndex ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                                background: idx === selectedAddressIndex ? '#eff6ff' : '#fafafa',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '12px'
                                            }}
                                        >
                                            <span style={{
                                                fontSize: '13px',
                                                color: '#1f2937',
                                                fontWeight: idx === selectedAddressIndex ? 500 : 400
                                            }}>
                                                {dir.direccion || dir.direccion_completa}
                                            </span>
                                            {dir.es_principal && (
                                                <span style={{
                                                    padding: '3px 8px',
                                                    background: '#dcfce7',
                                                    color: '#15803d',
                                                    borderRadius: '4px',
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    flexShrink: 0,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    Principal
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Opciones de entrega */}
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                {/* Opción Domicilio */}
                                <div
                                    onClick={() => {
                                        setDeliveryType('domicilio');
                                        setTimeout(() => setCurrentStep(3), 300);
                                    }}
                                    style={{
                                        padding: '16px',
                                        borderRadius: '10px',
                                        border: deliveryType === 'domicilio' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                        background: deliveryType === 'domicilio' ? '#eff6ff' : 'white',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '50%',
                                        background: deliveryType === 'domicilio' ? '#3b82f6' : '#f1f5f9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 10px auto',
                                        transition: 'all 0.2s ease'
                                    }}>
                                        <Truck size={22} color={deliveryType === 'domicilio' ? 'white' : '#64748b'} />
                                    </div>
                                    <h4 style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        margin: '0 0 4px 0',
                                        color: '#1f2937'
                                    }}>
                                        A Domicilio
                                    </h4>
                                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px 0' }}>
                                        Entrega en dirección del cliente
                                    </p>
                                    <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                                        <kbd style={{
                                            padding: '2px 6px',
                                            background: '#f1f5f9',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            border: '1px solid #e2e8f0'
                                        }}>1</kbd>
                                        <span style={{ color: '#9ca3af', fontSize: '11px' }}>o</span>
                                        <kbd style={{
                                            padding: '2px 6px',
                                            background: '#f1f5f9',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            border: '1px solid #e2e8f0'
                                        }}>D</kbd>
                                    </div>
                                </div>

                                {/* Opción Pickup */}
                                <div
                                    onClick={() => {
                                        setDeliveryType('pickup');
                                        setSelectedAddress(null);
                                        setTimeout(() => setCurrentStep(3), 300);
                                    }}
                                    style={{
                                        padding: '16px',
                                        borderRadius: '10px',
                                        border: deliveryType === 'pickup' ? '2px solid #10b981' : '1px solid #e5e7eb',
                                        background: deliveryType === 'pickup' ? '#ecfdf5' : 'white',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '50%',
                                        background: deliveryType === 'pickup' ? '#10b981' : '#f1f5f9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 10px auto',
                                        transition: 'all 0.2s ease'
                                    }}>
                                        <Package size={22} color={deliveryType === 'pickup' ? 'white' : '#64748b'} />
                                    </div>
                                    <h4 style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        margin: '0 0 4px 0',
                                        color: '#1f2937'
                                    }}>
                                        Pasan por Él
                                    </h4>
                                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px 0' }}>
                                        El cliente recogerá en tienda
                                    </p>
                                    <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                                        <kbd style={{
                                            padding: '2px 6px',
                                            background: '#f1f5f9',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            border: '1px solid #e2e8f0'
                                        }}>2</kbd>
                                        <span style={{ color: '#9ca3af', fontSize: '11px' }}>o</span>
                                        <kbd style={{
                                            padding: '2px 6px',
                                            background: '#f1f5f9',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            border: '1px solid #e2e8f0'
                                        }}>P</kbd>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Botones de navegación */}
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'center',
                            paddingTop: '4px'
                        }}>
                            <Button
                                variant="outline"
                                onClick={() => setCurrentStep(1)}
                                style={{ padding: '8px 20px', fontSize: '13px' }}
                            >
                                Atrás
                            </Button>
                            <Button
                                onClick={() => setCurrentStep(3)}
                                disabled={!deliveryType}
                                style={{ padding: '8px 20px', fontSize: '13px' }}
                            >
                                Continuar
                            </Button>
                        </div>

                        {/* Ayuda de atajos */}
                        <div style={{
                            textAlign: 'center',
                            padding: '10px 16px',
                            background: '#f8fafc',
                            borderRadius: '8px'
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: '12px',
                                color: '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '12px',
                                flexWrap: 'wrap'
                            }}>
                                <span><kbd style={{ padding: '2px 5px', background: '#e2e8f0', borderRadius: '3px', fontSize: '11px' }}>1</kbd>/<kbd style={{ padding: '2px 5px', background: '#e2e8f0', borderRadius: '3px', fontSize: '11px' }}>D</kbd> Domicilio</span>
                                <span style={{ color: '#cbd5e1' }}>|</span>
                                <span><kbd style={{ padding: '2px 5px', background: '#e2e8f0', borderRadius: '3px', fontSize: '11px' }}>2</kbd>/<kbd style={{ padding: '2px 5px', background: '#e2e8f0', borderRadius: '3px', fontSize: '11px' }}>P</kbd> Pickup</span>
                                <span style={{ color: '#cbd5e1' }}>|</span>
                                <span><kbd style={{ padding: '2px 5px', background: '#e2e8f0', borderRadius: '3px', fontSize: '11px' }}>↑↓</kbd> Direcciones</span>
                            </p>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="counter-sale-page new-sale-form-table" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        {/* Barra de búsqueda */}
                        <div className="search-section-table">
                            <div className="search-box-large" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{ flex: 1, position: 'relative' }}>
                                    <Input
                                        ref={searchInputRef}
                                        placeholder="Buscar producto por nombre o código de barras..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        icon={<Search size={24} />}
                                        autoComplete="off"
                                    />
                                    {showResults && searchResults.length > 0 && (
                                        <div className="search-results-dropdown" ref={searchResultsContainerRef}>
                                            <div className="search-hint">
                                                Use ↑↓ para navegar, <kbd>Enter</kbd> para agregar
                                            </div>
                                            {searchResults.map((producto, index) => (
                                                <div
                                                    key={producto.id}
                                                    className={`search-result-item compact ${selectedSearchResultIndex === index ? 'keyboard-focused' : ''}`}
                                                    onClick={() => {
                                                        handleSelectProduct(producto, false);
                                                        setSearchQuery('');
                                                        setShowResults(false);
                                                    }}
                                                    onMouseEnter={() => setSelectedSearchResultIndex(index)}
                                                >
                                                    {producto.codigo_corto && (
                                                        <span className="result-code-short">{producto.codigo_corto}</span>
                                                    )}
                                                    <span className="result-name">{producto.nombre}</span>
                                                    <span className="result-price">{formatMoney(getCustomerPrice(producto))}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {showResults && searchResults.length === 0 && searchQuery.length >= 2 && (
                                        <div className="search-results-dropdown">
                                            <div className="no-results">No se encontraron productos</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Tabla de productos */}
                        <div className="products-table-container" style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                            {cart.length === 0 ? (
                                <div className="empty-cart-table">
                                    <Package size={64} />
                                    <p>No hay productos en el pedido</p>
                                    <span>Busca y agrega productos para continuar</span>
                                </div>
                            ) : (
                                <table className="products-table" style={{ tableLayout: 'auto', width: '100%' }}>
                                    <colgroup>
                                        <col style={{ width: '10%' }} />
                                        <col style={{ width: '10%' }} />
                                        <col />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '5%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>Cantidad</th>
                                            <th>Artículo</th>
                                            <th style={{ textAlign: 'left' }}>Descripción</th>
                                            <th>Unidad</th>
                                            <th style={{ textAlign: 'right' }}>Precio</th>
                                            <th>Total</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.map(item => (
                                            <tr
                                                key={item.id}
                                                onClick={() => setSelectedCartItemId(item.id)}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedCartItemId === item.id ? '#eff6ff' : 'transparent',
                                                    outline: selectedCartItemId === item.id ? '2px solid #3b82f6' : 'none',
                                                    outlineOffset: '-2px',
                                                    transition: 'background-color 0.15s, outline 0.15s'
                                                }}
                                            >
                                                <td>
                                                    <div className="quantity-controls-table">
                                                        <button className="qty-btn-table" onClick={() => removeFromCart(item.id)}>
                                                            <Minus size={14} />
                                                        </button>
                                                        <input
                                                            type="number"
                                                            className="qty-input-table"
                                                            value={item.cantidad}
                                                            onChange={(e) => updateQuantityDebounced(item.id, e.target.value)}
                                                            onFocus={(e) => e.target.select()}
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                        <button className="qty-btn-table" onClick={() => updateQuantity(item.id, parseFloat(item.cantidad) + 1)}>
                                                            <Plus size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="article-code">{item.codigo_corto || item.producto_id}</span>
                                                </td>
                                                <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                                                    <span className="product-description">{item.producto_nombre || item.nombre}</span>
                                                </td>
                                                <td>
                                                    {item.equivalencias_disponibles && item.equivalencias_disponibles.length > 1 ? (
                                                        <select
                                                            value={item.producto_id}
                                                            onChange={(e) => {
                                                                const nuevaPresentacion = item.equivalencias_disponibles.find(
                                                                    eq => eq.producto_id === parseInt(e.target.value)
                                                                );
                                                                if (nuevaPresentacion) {
                                                                    cambiarPresentacion(item.id, nuevaPresentacion);
                                                                }
                                                            }}
                                                            style={{
                                                                fontSize: '15px',
                                                                padding: '6px 8px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #3b82f6',
                                                                background: '#eff6ff',
                                                                color: '#1e40af',
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                                maxWidth: '100px'
                                                            }}
                                                            title="Cambiar presentación"
                                                        >
                                                            {item.equivalencias_disponibles.map((eq) => (
                                                                <option key={eq.producto_id} value={eq.producto_id}>
                                                                    {eq.unidad_medida || 'pz'}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span className="unit-label" style={{ fontSize: '16px', color: '#6b7280', fontWeight: 500 }}>
                                                            {item.unidad_medida || 'pz'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-right" style={{ position: 'relative' }}>
                                                    <div className="price-selector-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                                                        <button
                                                            onClick={(e) => handlePriceClick(item, e)}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                color: '#667eea',
                                                                fontWeight: 600,
                                                                padding: '6px 10px',
                                                                borderRadius: '4px',
                                                                transition: 'background 0.2s',
                                                                fontSize: '17px'
                                                            }}
                                                            title="Click para cambiar precio"
                                                        >
                                                            {formatMoney(item.precio_unitario * (1 + (comisionEnvioSeleccionada?.valor || 0) / 100))}
                                                        </button>
                                                        {item.descuento_volumen > 0 && (
                                                            <span style={{
                                                                fontSize: '13px',
                                                                fontWeight: 600,
                                                                padding: '3px 8px',
                                                                borderRadius: '4px',
                                                                color: 'white',
                                                                backgroundColor: '#16a34a',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                -{item.descuento_volumen}%
                                                            </span>
                                                        )}
                                                        {item.precio_tipo && (
                                                            <span style={{
                                                                fontSize: '13px',
                                                                fontWeight: 600,
                                                                padding: '3px 8px',
                                                                borderRadius: '4px',
                                                                color: 'white',
                                                                backgroundColor:
                                                                    item.precio_tipo === 'precio_libre' ? '#dc2626' :
                                                                        item.precio_tipo === 'precio_lista' ? '#0284c7' :
                                                                            item.precio_tipo === 'precio1' ? '#1e40af' :
                                                                                item.precio_tipo === 'precio2' ? '#059669' :
                                                                                    item.precio_tipo === 'precio3' ? '#b45309' :
                                                                                        item.precio_tipo === 'precio4' ? '#be123c' :
                                                                                            item.precio_tipo === 'precio5' ? '#0891b2' :
                                                                                                item.precio_tipo === 'precio6' ? '#7e22ce' : '#0284c7',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.5px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {item.precio_tipo === 'precio_libre' ? 'LIBRE' :
                                                                    item.precio_tipo === 'precio_lista' ? 'PL' :
                                                                        item.precio_tipo === 'precio1' ? 'P1' :
                                                                            item.precio_tipo === 'precio2' ? 'P2' :
                                                                                item.precio_tipo === 'precio3' ? 'P3' :
                                                                                    item.precio_tipo === 'precio4' ? 'P4' :
                                                                                        item.precio_tipo === 'precio5' ? 'P5' :
                                                                                            item.precio_tipo === 'precio6' ? 'P6' : 'PL'}
                                                            </span>
                                                        )}
                                                        {/* Selector de precios inline */}
                                                        {priceSelector && priceSelector.productId === item.id && (() => {
                                                            const precios = priceSelector.precios;
                                                            const preciosConfig = [
                                                                { tipo: 'precio_lista', label: 'Precio Lista', valor: precios.precio_lista, color: '#0284c7', bgSelected: '#e0f2fe' },
                                                                { tipo: 'precio1', label: 'Precio 1', valor: precios.precio1, color: '#1e40af', bgSelected: '#eff6ff' },
                                                                { tipo: 'precio2', label: 'Precio 2', valor: precios.precio2, color: '#065f46', bgSelected: '#d1fae5' },
                                                                { tipo: 'precio3', label: 'Precio 3', valor: precios.precio3, color: '#92400e', bgSelected: '#fef3c7' },
                                                                { tipo: 'precio4', label: 'Precio 4', valor: precios.precio4, color: '#9f1239', bgSelected: '#fce7f3' },
                                                                { tipo: 'precio5', label: 'Precio 5', valor: precios.precio5, color: '#0891b2', bgSelected: '#cffafe' },
                                                                { tipo: 'precio6', label: 'Precio 6', valor: precios.precio6, color: '#7e22ce', bgSelected: '#f3e8ff' }
                                                            ].filter(p => p.valor > 0);

                                                            return (
                                                                <div style={{
                                                                    position: 'fixed',
                                                                    top: priceSelector.position?.top ? `${priceSelector.position.top}px` : '50%',
                                                                    left: priceSelector.position?.left ? `${priceSelector.position.left}px` : '50%',
                                                                    transform: priceSelector.position ? 'none' : 'translate(-50%, -50%)',
                                                                    background: 'white',
                                                                    border: '1px solid #e5e7eb',
                                                                    borderRadius: '8px',
                                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                                    zIndex: 10000,
                                                                    minWidth: '250px'
                                                                }}>
                                                                    <div style={{
                                                                        padding: '12px 14px',
                                                                        borderBottom: '1px solid #e5e7eb',
                                                                        background: '#f9fafb'
                                                                    }}>
                                                                        <div style={{
                                                                            fontSize: '14px',
                                                                            fontWeight: 600,
                                                                            color: '#374151',
                                                                            marginBottom: '4px',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            maxWidth: '280px'
                                                                        }}>
                                                                            {item.producto_nombre || item.nombre}
                                                                        </div>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <span style={{ fontSize: '13px', color: '#6b7280' }}>Seleccionar precio</span>
                                                                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>↑↓ Enter Esc</span>
                                                                        </div>
                                                                    </div>
                                                                    {preciosConfig.map((precio, index) => (
                                                                        <button
                                                                            key={precio.tipo}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                changePriceLevel(item.id, precio.valor, precio.tipo);
                                                                                setPriceSelector(null);
                                                                            }}
                                                                            onMouseEnter={() => setSelectedPriceIndex(index)}
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '10px 12px',
                                                                                border: 'none',
                                                                                background: selectedPriceIndex === index ? '#3b82f6' :
                                                                                    (priceSelector.currentPrice === precio.valor ? precio.bgSelected : 'white'),
                                                                                textAlign: 'left',
                                                                                cursor: 'pointer',
                                                                                fontSize: '16px',
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                borderBottom: '1px solid #f3f4f6',
                                                                                outline: 'none'
                                                                            }}
                                                                        >
                                                                            <span style={{ color: selectedPriceIndex === index ? 'white' : precio.color, fontWeight: 500 }}>
                                                                                {precio.label}
                                                                            </span>
                                                                            <span style={{ fontWeight: 600, color: selectedPriceIndex === index ? 'white' : '#1f2937' }}>
                                                                                {formatMoney(precio.valor)}
                                                                            </span>
                                                                        </button>
                                                                    ))}
                                                                    <button
                                                                        onClick={() => setPriceSelector(null)}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '10px 14px',
                                                                            border: 'none',
                                                                            background: 'white',
                                                                            textAlign: 'center',
                                                                            cursor: 'pointer',
                                                                            fontSize: '15px',
                                                                            color: '#6b7280',
                                                                            fontWeight: 500
                                                                        }}
                                                                    >
                                                                        Cancelar
                                                                    </button>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </td>
                                                <td className="text-right font-semibold">
                                                    {formatMoney(item.cantidad * item.precio_unitario * (1 + (comisionEnvioSeleccionada?.valor || 0) / 100))}
                                                </td>
                                                <td className="text-center">
                                                    <button className="delete-btn-table" onClick={() => deleteFromCart(item.id)} title="Eliminar">
                                                        <Trash2 size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Footer - siempre al fondo */}
                        <div className="sale-footer-table" style={{ display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'flex-start', alignItems: 'stretch', marginTop: 'auto', flexShrink: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '20px', width: '100%' }}>
                                {/* Observaciones */}
                                <div style={{ flex: '1', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                        Observaciones:
                                    </label>
                                    <textarea
                                        value={observaciones}
                                        onChange={(e) => setObservaciones(e.target.value)}
                                        placeholder="Instrucciones especiales..."
                                        rows={2}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontFamily: 'inherit',
                                            resize: 'vertical',
                                            minHeight: '60px',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                {/* Totales y acciones */}
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '20px' }}>
                                    {/* Totales */}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                        <div className="total-display">
                                            <span className="total-label">Total:</span>
                                            <span className="total-amount">
                                                {formatMoney(totals.total * (1 + (comisionEnvioSeleccionada?.valor || 0) / 100))}
                                            </span>
                                        </div>
                                        {totals.tienePrecioMayoreo && comisionTarjeta > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <CreditCard size={14} color="#d97706" />
                                                <span style={{ fontSize: '12px', color: '#92400e' }}>Con tarjeta:</span>
                                                <span style={{ fontSize: '14px', color: '#b45309', fontWeight: 600 }}>
                                                    {formatMoney(totals.totalConComision * (1 + (comisionEnvioSeleccionada?.valor || 0) / 100))}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                            {cart.length} {cart.length === 1 ? 'producto' : 'productos'}
                                        </div>
                                    </div>

                                    {/* Botones */}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Button
                                            variant="outline"
                                            onClick={handlePausar}
                                            disabled={cart.length === 0}
                                            style={{ padding: '12px 16px' }}
                                        >
                                            Pausar (F5)
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleProcessSale}
                                            disabled={cart.length === 0 || loading}
                                            style={{ padding: '12px 24px', fontSize: '16px', fontWeight: 600 }}
                                        >
                                            Cobrar (F10)
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Leyenda de atajos */}
                            <div style={{ textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', paddingBottom: '0.25rem' }}>
                                <p style={{ margin: 0, fontSize: '1rem', color: '#000000', lineHeight: '1.5' }}>
                                    <kbd>F1</kbd>: Buscar | <kbd>F2</kbd>: Cliente | <kbd>F3</kbd>: Entrega | <kbd>F4</kbd>: Precio | <kbd>F5</kbd>: Pausar | <kbd>F10</kbd>: Pagar | <kbd>F11</kbd>: Unidad | <kbd>↑↓</kbd>: Navegar | <kbd>+/-</kbd>: Cantidad | <kbd>Del</kbd>: Eliminar
                                </p>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div style={{ cursor: cursorHidden ? 'none' : 'auto' }}>
            <Modal
                isOpen={true}
                onClose={onClose}
                title={renderModalTitle()}
                size={currentStep >= 3 ? "fullscreen" : "large"}
                closeOnOverlayClick={false}
                className={currentStep === 1 && !showQuickRegister ? 'modal-client-search' : ''}
                style={{ cursor: cursorHidden ? 'none' : 'auto' }}
            >
                <div style={{ cursor: cursorHidden ? 'none' : 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {renderStep()}
                </div>
            </Modal>

            {/* Modales externos */}
            <QuantityModal
                isOpen={!!quantityModalData}
                onClose={() => {
                    setQuantityModalData(null);
                    setEquivalenciasDisponibles([]);
                    searchInputRef.current?.focus();
                }}
                onConfirm={handleConfirmQuantity}
                producto={quantityModalData?.producto}
                initialQuantity={1}
                equivalencias={equivalenciasDisponibles}
                loadingEquivalencias={loadingEquivalencias}
                getCustomerPriceFromPresentacion={getCustomerPriceFromPresentacion}
            />

            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                onConfirm={handleConfirmPayment}
                total={totals.total * (1 + (comisionEnvioSeleccionada?.valor || 0) / 100)}
                totalConComision={totals.totalConComision * (1 + (comisionEnvioSeleccionada?.valor || 0) / 100)}
                tienePrecioMayoreo={totals.tienePrecioMayoreo}
                comisionTarjeta={comisionTarjeta}
                cliente={cliente}
                creditoDisponible={creditoDisponible}
                showToast={showToast}
                showCredito={true}
                showEstatusPago={true}
                estatusPago={estatusPago}
                onEstatusPagoChange={setEstatusPago}
            />

            <PrintOrderModal
                isOpen={showPrintModal}
                onClose={handlePrintModalClose}
                onPrintComplete={handlePrintModalClose}
                ventaData={ventaParaImprimir}
                empresaLogo={empresaLogo}
                showToast={showToast}
                tipoImpresionDefault={tipoImpresionPedido}
                impresoraPedidos={impresoraPedidos}
            />

            {/* Modal de contraseña para precio libre */}
            {showPasswordPrecioLibreModal && (
                <Modal
                    title="Autorización Requerida"
                    onClose={cerrarPrecioLibre}
                    size="small"
                >
                    <div style={{ padding: '20px' }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            padding: '16px',
                            background: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid #f59e0b'
                        }}>
                            <Edit3 size={32} color="#d97706" style={{ marginBottom: '8px' }} />
                            <p style={{ margin: 0, color: '#92400e', fontWeight: 600 }}>
                                Precio Libre
                            </p>
                            <p style={{ margin: '8px 0 0', color: '#a16207', fontSize: '14px' }}>
                                Ingresa la contraseña de un usuario autorizado
                            </p>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>
                                Producto: {precioLibreData.productoNombre}
                            </label>
                        </div>

                        <Input
                            ref={passwordPrecioLibreInputRef}
                            type="password"
                            placeholder="Contraseña de autorización"
                            value={passwordPrecioLibre}
                            onChange={(e) => setPasswordPrecioLibre(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleVerificarPasswordPrecioLibre();
                            }}
                            disabled={verificandoPasswordPrecioLibre}
                        />

                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <Button
                                variant="secondary"
                                onClick={cerrarPrecioLibre}
                                disabled={verificandoPasswordPrecioLibre}
                                style={{ flex: 1 }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleVerificarPasswordPrecioLibre}
                                loading={verificandoPasswordPrecioLibre}
                                disabled={verificandoPasswordPrecioLibre || !passwordPrecioLibre}
                                style={{ flex: 1 }}
                            >
                                Verificar
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de precio libre */}
            {showPrecioLibreModal && (
                <Modal
                    title="Capturar Precio Libre"
                    onClose={cerrarPrecioLibre}
                    size="small"
                >
                    <div style={{ padding: '20px' }}>
                        <div style={{
                            marginBottom: '20px',
                            padding: '16px',
                            background: '#f0fdf4',
                            borderRadius: '8px',
                            border: '1px solid #86efac'
                        }}>
                            <p style={{ margin: 0, fontWeight: 600, color: '#166534' }}>
                                {precioLibreData.productoNombre}
                            </p>
                            <p style={{ margin: '8px 0 0', color: '#15803d', fontSize: '14px' }}>
                                Precio mínimo (costo): <strong>{formatMoney(precioLibreData.precioCosto)}</strong>
                            </p>
                            {usuarioAutorizadorPrecioLibre && (
                                <p style={{ margin: '8px 0 0', color: '#059669', fontSize: '13px' }}>
                                    Autorizado por: {usuarioAutorizadorPrecioLibre}
                                </p>
                            )}
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>
                                Nuevo Precio:
                            </label>
                            <Input
                                ref={precioLibreInputRef}
                                type="number"
                                placeholder="0.00"
                                value={precioLibreData.precio}
                                onChange={(e) => setPrecioLibreData(prev => ({ ...prev, precio: e.target.value }))}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleAplicarPrecioLibre();
                                }}
                                min={precioLibreData.precioCosto}
                                step="0.01"
                                style={{ fontSize: '24px', textAlign: 'center', fontWeight: 600 }}
                            />
                            {precioLibreData.precio && parseFloat(precioLibreData.precio) < precioLibreData.precioCosto && (
                                <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>
                                    El precio no puede ser menor al costo
                                </p>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button
                                variant="secondary"
                                onClick={cerrarPrecioLibre}
                                style={{ flex: 1 }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleAplicarPrecioLibre}
                                disabled={!precioLibreData.precio || parseFloat(precioLibreData.precio) < precioLibreData.precioCosto}
                                style={{ flex: 1, background: '#059669' }}
                            >
                                Aplicar Precio
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            {/* Modal de historial producto-cliente (Ctrl+H) */}
            {showHistorialModal && (
                <Modal
                    title="Historial de Ventas"
                    onClose={() => setShowHistorialModal(false)}
                    size="medium"
                >
                    <div style={{ padding: '24px' }}>
                        <div style={{
                            marginBottom: '16px',
                            padding: '12px 16px',
                            background: '#eff6ff',
                            borderRadius: '8px',
                            border: '1px solid #bfdbfe'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <History size={18} color="#2563eb" />
                                <span style={{ fontWeight: 600, color: '#1e40af' }}>{historialProductoNombre}</span>
                            </div>
                            <div style={{ fontSize: '13px', color: '#3b82f6' }}>
                                Cliente: <strong>{cliente?.nombre}</strong> — Últimas 5 ventas
                            </div>
                        </div>

                        {/* Costo actual del producto */}
                        {!loadingHistorial && historialCostoActual > 0 && (
                            <div style={{
                                marginBottom: '16px',
                                padding: '10px 16px',
                                background: '#fefce8',
                                borderRadius: '8px',
                                border: '1px solid #fde68a',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span style={{ fontSize: '14px', color: '#92400e' }}>
                                    Costo actual ({historialCostoTipoActual === 'costo_precios' ? 'Costo Precios' : 'Precio Compra'}):
                                </span>
                                <span style={{ fontSize: '18px', fontWeight: 700, color: '#b45309' }}>
                                    {formatMoney(historialCostoActual)}
                                </span>
                            </div>
                        )}

                        {loadingHistorial ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                Cargando historial...
                            </div>
                        ) : historialData.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                <History size={48} style={{ marginBottom: '12px', opacity: 0.4 }} />
                                <p>No hay ventas previas de este producto a este cliente</p>
                            </div>
                        ) : (
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '15px'
                            }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                        <th style={{ padding: '10px 8px', textAlign: 'left', color: '#374151', fontWeight: 600 }}>Fecha</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'center', color: '#374151', fontWeight: 600 }}>Cant.</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>Precio</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'center', color: '#374151', fontWeight: 600 }}>Tipo</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>Costo</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historialData.map((h, idx) => (
                                        <tr key={idx} style={{
                                            borderBottom: '1px solid #f3f4f6',
                                            background: idx % 2 === 0 ? 'white' : '#f9fafb'
                                        }}>
                                            <td style={{ padding: '10px 8px', color: '#374151' }}>
                                                <div>{new Date(h.fecha).toLocaleDateString('es-MX')}</div>
                                                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                                    {new Date(h.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, color: '#1f2937' }}>
                                                {h.cantidad}
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                                                <button
                                                    onClick={() => aplicarPrecioHistorial(h.precio_unitario)}
                                                    title="Click para aplicar este precio"
                                                    style={{
                                                        background: 'none',
                                                        border: '1px dashed #059669',
                                                        borderRadius: '4px',
                                                        padding: '4px 10px',
                                                        cursor: 'pointer',
                                                        color: '#059669',
                                                        fontWeight: 700,
                                                        fontSize: '15px',
                                                        transition: 'all 0.15s'
                                                    }}
                                                    onMouseEnter={(e) => { e.target.style.background = '#ecfdf5'; e.target.style.borderStyle = 'solid'; }}
                                                    onMouseLeave={(e) => { e.target.style.background = 'none'; e.target.style.borderStyle = 'dashed'; }}
                                                >
                                                    {formatMoney(h.precio_unitario)}
                                                </button>
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                                <span style={{
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    color: 'white',
                                                    backgroundColor:
                                                        h.precio_tipo === 'precio_lista' ? '#0284c7' :
                                                        h.precio_tipo === 'precio1' ? '#1e40af' :
                                                        h.precio_tipo === 'precio2' ? '#059669' :
                                                        h.precio_tipo === 'precio3' ? '#b45309' :
                                                        h.precio_tipo === 'precio_libre' ? '#dc2626' : '#6b7280'
                                                }}>
                                                    {h.precio_tipo === 'precio_lista' ? 'PL' :
                                                     h.precio_tipo === 'precio1' ? 'P1' :
                                                     h.precio_tipo === 'precio2' ? 'P2' :
                                                     h.precio_tipo === 'precio3' ? 'P3' :
                                                     h.precio_tipo === 'precio4' ? 'P4' :
                                                     h.precio_tipo === 'precio5' ? 'P5' :
                                                     h.precio_tipo === 'precio6' ? 'P6' :
                                                     h.precio_tipo === 'precio_libre' ? 'LIBRE' :
                                                     h.precio_tipo || 'N/A'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                                                {h.costo > 0 ? (
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: '#b45309' }}>
                                                            {formatMoney(h.costo)}
                                                        </div>
                                                        {historialCostoActual > 0 && h.costo !== historialCostoActual && (
                                                            <div style={{
                                                                fontSize: '11px',
                                                                color: h.costo < historialCostoActual ? '#dc2626' : '#16a34a',
                                                                fontWeight: 600
                                                            }}>
                                                                {h.costo < historialCostoActual ? '▲' : '▼'} {formatMoney(Math.abs(historialCostoActual - h.costo))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, color: '#1f2937' }}>
                                                {formatMoney(h.total)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '20px',
                            paddingTop: '16px',
                            borderTop: '1px solid #e5e7eb'
                        }}>
                            <Button variant="secondary" onClick={() => setShowHistorialModal(false)}>
                                Cerrar
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default NewSaleFormRefactored;
